import {
  ChevronLeft,
  ChevronRight,
  Dot,
  User,
  CheckCircle,
  Download,
  SquarePen,
  Signature,
  FileText,
  History,
  Siren,
  Bell,
  InfoIcon,
  Info,
  UserCheck,
  Trash,
  Plus,
  Eye,
  X,
} from "lucide-react";
import { useState } from "react";
import {
  CopyDataCard,
  CopyDataLocation,
  DataCard,
} from "../../shared/DataCard";
import ActionCard from "../../shared/ActionCard";
import { useNavigate, useParams } from "react-router-dom";
import SingleLoanSkeleton from "../../skeletons/SingleLoanSkeleton";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useMutation, useQuery } from "react-query";
import {
  addGuarantorChattel,
  addLoanApplicationDocument,
  addLoanGuarantorDocument,
  createApplicationGuarantors,
  deleteLoanApplicationDocument,
  getGuarantorDocuments,
  getLoanApplicationApprovals,
  getLoanApplicationById,
  reassignALoanOfficer,
  updateApplicationGuarantor,
  updateApplicationGuarantorLocation,
} from "../../sdk/applications/applications";
import decimalToDMS from "../../utils/location";
import downloadFromUrl from "../../utils/download";
import Prompt from "../../components/approvals/Prompt";
import LoanDetails from "../../components/approvals/LoanDetails";
import CheckList from "../../components/approvals/CheckList";
import ApprovedAmount from "../../components/approvals/ApprovedAmount";
import Finalize from "../../components/approvals/Finalize";
import ApproveSuccess from "../../components/approvals/ApproveSuccess";
import FinalizeDecline from "../../components/approvals/FinalizeDecline";
import DeclineSuccess from "../../components/approvals/DeclineSuccess";
import { approveApplication } from "../../sdk/approve/approve";
import useAuth from "../../hooks/useAuth";
import { useError } from "../../context/ErrorProvider";
import EditPersonalDetails from "../../components/edit-borrower/edit-personal-details/EditPersonalDetails";
import EditKin from "../../components/edit-borrower/edit-kin/EditKin";
import EditIncomeDetails from "../../components/edit-borrower/edit-income/EditIncomeDetails";
import {
  addBankStatement,
  addBorrowerChattel,
  addMpesaStatement,
  addPayslips,
  editBorrowerPersonalDetails,
  editIncomeDetails,
  editMyBorrowerBusinessDetails,
  editMyBorrowerChattel,
  editNextOfKinDetails,
  patchBorrowerResidenceDetails,
  updateBorrower,
  uploadBorrowerSelfie,
} from "../../sdk/borrower/borrower";
import EditPersonalDetailsSuccess from "../../components/edit-borrower/edit-personal-details/EditPersonalDetailsSuccess";
import EditKinSuccess from "../../components/edit-borrower/edit-kin/EditKinSuccess";
import EditIncomeSuccess from "../../components/edit-borrower/edit-income/EditIncomeSuccess";
import DeclineChecklist from "../../components/approvals/DeclineChecklist";
import EditBusinessLocation from "../../components/edit-borrower/edit-business-location/EditBusinessLocation";
import EditResidenceLocation from "../../components/edit-borrower/edit-residence-location/EditResidenceLocation";
import ReassignOfficer from "../../components/reassign-officer/ReassignOfficer";
import ReassignPrompt from "../../components/reassign-officer/ReassignPrompt";
import ReaasignSuccess from "../../components/reassign-officer/ReaasignSuccess";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import EditGuarantor from "../../components/edit-application/edit-guarantor/EditGuarantor";
import EditGuarantorChattel from "../../components/edit-application/edit-guarantor-chattel/EditGuarantorChattel";
import EditGuarantorChattelSuccess from "../../components/edit-application/edit-guarantor-chattel/EditGuarantorChattelSuccess";
import EditChattel from "../../components/edit-borrower/edit-chattel/EditChattel";
import EditLoanDetails from "../../components/edit-application/edit-loan-details/EditLoanDetails";
import EditLoanDetailsSuccess from "../../components/edit-application/edit-loan-details/EditLoanDetailsSuccess";
import AllDocuments from "../../components/edit-application/edit-documents/AllDocuments";
import EditAddDocument from "../../components/edit-application/edit-documents/EditAddDocument";
import LoanChattels from "../../components/edit-application/add-loan-chattel/LoanChattels";
import AddLoanChattel from "../../components/edit-application/add-loan-chattel/AddLoanChattel";
import EditBusinessLocationSuccess from "../../components/edit-borrower/edit-business-location/EditBusinessLocationSuccess";
import EditResidenceLocationSuccess from "../../components/edit-borrower/edit-residence-location/EditResidenceLocationSuccess";
import EditGuarantorSuccess from "../../components/edit-application/edit-guarantor/EditGuarantorSuccess";
import AddLoanGuarantorChattel from "../../components/edit-application/add-guarantor-chattel/AddLoanGuarantorChattel";
import EditGuarantorLocation from "../../components/edit-application/edit-guarantor-location/EditGuarantorLocation";
import EditGuarantorLocationSuccess from "../../components/edit-application/edit-guarantor-location/EditGuarantorLocationSuccess";
import { hasPermission } from "../../utils/permissions";
import EditChattelSuccess from "../../components/edit-borrower/edit-chattel/EditChattelSuccess";
import AddLoanGuarantor from "../../components/edit-application/add-loan-guarantor/AddLoanGuarantor";
import AddLoanGuarantorSuccess from "../../components/edit-application/add-loan-guarantor/AddLoanGuarantorSuccess";
import Chattel from "../../components/view-chattel/Chattel";
import AddNationalId from "../../components/add-national-id/AddNationalId";
import AddNationalIdSuccess from "../../components/add-national-id/AddNationalIdSuccess";
import AddPayslips from "../../components/add-payslips/AddPayslips";
import AddMpesaStatement from "../../components/add-mpesa-statement/AddMpesaStatement";
import AddBankStatement from "../../components/add-bank-statement/AddBankStatement";
import AddBankStatementSuccess from "../../components/add-bank-statement/AddBankStatementSuccess";
import AddMpesaStatementSuccess from "../../components/add-mpesa-statement/AddMpesaStatementSuccess";
import AddPayslipsSuccess from "../../components/add-payslips/AddPayslipsSuccess";
import UploadSelfie from "../../components/add-selfie/UploadSelfie";
import AddSelfieSuccess from "../../components/add-selfie/AddSelfieSuccess";
import GuarantorDocuments from "../../components/add-guarantor-documents/GuarantorDocuments";
import AddGuarantorDocument from "../../components/add-guarantor-documents/AddGuarantorDocument";
import { AnimatePresence, motion } from "framer-motion";

const LoanApplication = () => {
  const [activeTab, setActiveTab] = useState("Borrower");
  const [openIndexes, setOpenIndexes] = useState([]);
  const navigate = useNavigate();
  const formatAmount = useFormatAmount();
  const formattedDate = useFormattedDate();
  const { id } = useParams();
  const { auth } = useAuth();
  const [approvals, setApprovals] = useState({});
  const [openPromptApproval, setOpenPromptApproval] = useState(false);
  const [openLoanDetails, setOpenLoanDetails] = useState(false);
  const [openDeclineChecklist, setOpenDeclineChecklist] = useState(false);
  const [openChecklist, setOpenChecklist] = useState(false);
  const [openApprovedAmount, setOpenApprovedAmount] = useState(false);
  const [openFinalizeApproval, setOpenFinalizeApproval] = useState(false);
  const [openApproveSuccess, setOpenApproveSuccess] = useState(false);
  const [openFinalizeDecline, setOpenFinalizeDecline] = useState(false);
  const [openDeclineSuccess, setOpenDeclineSuccess] = useState(false);
  const [approvalDetails, setApprovalDetails] = useState({
    checklist: {},
    approvalAmount: "",
    description: "",
    additionalDocuments: [],
  });
  const [generalStatus, setGeneralStatus] = useState("");
  const { showError } = useError();
  const [openEditPersonalDetails, setOpenEditPersonalDetails] = useState(false);
  const [borrowerEditDetails, setBorrowerEditDetails] = useState({
    firstname: "",
    middlename: "",
    lastname: "",
    identification: "",
    dob: "",
    email: "",
    mobileno: "",
    altPhoneNumber: "",
    gender: "",
    maritalStatus: "",
    postal_address: "",
    dependants: "",
  });
  const [openReassignOfficer, setOpenReassignOfficer] = useState(false);
  const [openReassignPrompt, setOpenReassignPrompt] = useState(false);
  const [openReassignSuccess, setOpenReassignSuccess] = useState(false);
  const [openEditGuarantor, setOpenEditGuarantor] = useState(false);
  const [openEditGuarantorSuccess, setOpenEditGuarantorSuccess] =
    useState(false);
  const [openEditGuarantorChattel, setOpenEditGuarantorChattel] =
    useState(false);
  const [openEditChattel, setOpenEditChattel] = useState(false);
  const [openEditChattelSuccess, setOpenEditChattelSuccess] = useState(false);
  const [openEditGuarantorChattelSuccess, setOpenEditGuarantorChattelSuccess] =
    useState(false);
  const [openEditLoanDetails, setOpenEditLoanDetails] = useState(false);
  const [openAllDocuments, setOpenAllDocuments] = useState(false);
  const [openAddGuarantorChattel, setOpenAddGuarantorChattel] = useState(false);
  const [openEditAddDocument, setOpenEditAddDocument] = useState(false);
  const [openAddLoanGuarantor, setOpenAddLoanGuarantor] = useState(false);
  const [openAddLoanGuarantorSuccess, setOpenAddLoanGuarantorSuccess] =
    useState(false);
  const [loanDocuments, setLoanDocuments] = useState([]);
  const [openEditGuarantorLocation, setOpenEditGuarantorLocation] =
    useState(false);
  const [
    openEditGuarantorLocationSuccess,
    setOpenEditGuarantorLocationSuccess,
  ] = useState(false);

  const toggleEditGuarantorLocation = (guarantor) => {
    setSelectedGuarantor(guarantor);
    setOpenEditGuarantorLocation(true);
  };
  const closeEditGuarantorLocation = () => {
    setOpenEditGuarantorLocation(false);
  };
  const toggleEditGuarantorLocationSuccess = () => {
    setOpenEditGuarantorLocationSuccess(true);
  };
  const closeEditGuarantorLocationSuccess = () => {
    setOpenEditGuarantorLocationSuccess(false);
  };

  const [myGuarantor, setMyGuarantor] = useState({
    guarantorName: "",
    guarantorPhone: "",
    guarantorEmail: "",
    residenceCounty: "",
    residenceSubCounty: "",
    residenceWard: "",
    residenceVillage: "",
    residenceLatitude: "",
    residenceLongitude: "",
    signatureUrl: "",
    guaranteeAmount: "",
    status: "",
    reason: "",
    guarantorPhoto: "",
    guarantorBusinessPhoto: "",
    residenceLocation: "",
  });

  const toggleAddLoanGuarantor = () => {
    setOpenAddLoanGuarantor(true);
  };
  const closeAddLoanGuarantor = () => {
    setOpenAddLoanGuarantor(false);
  };

  const toggleAddLoanGuarantorSuccess = () => {
    setOpenAddLoanGuarantorSuccess(true);
  };
  const closeAddLoanGuarantorSuccess = () => {
    setOpenAddLoanGuarantorSuccess(false);
  };

  const { mutate: addGuarantorMutate, isLoading: isLoadingGuarantor } =
    useMutation({
      mutationKey: ["add guarantor"],
      mutationFn: () =>
        createApplicationGuarantors(
          loanApplication?.id,
          myGuarantor.guarantorName,
          myGuarantor.guarantorPhone,
          myGuarantor.guarantorEmail,
          myGuarantor.residenceCounty,
          myGuarantor.residenceSubCounty,
          myGuarantor.residenceWard,
          myGuarantor.residenceVillage,
          myGuarantor.residenceLatitude,
          myGuarantor.residenceLongitude,
          myGuarantor.signatureUrl,
          myGuarantor.guaranteeAmount,
          myGuarantor.reason,
          myGuarantor?.guarantorPhoto,
          myGuarantor?.guarantorBusinessPhoto,
          myGuarantor?.residenceLocation,
        ),
      onSuccess: () => {
        setMyGuarantor({
          guarantorName: "",
          guarantorPhone: "",
          guarantorEmail: "",
          residenceCounty: "",
          residenceSubCounty: "",
          residenceWard: "",
          residenceVillage: "",
          residenceLatitude: "",
          residenceLongitude: "",
          signatureUrl: "",
          guaranteeAmount: "",
          status: "",
          reason: "",
          guarantorPhoto: "",
          guarantorBusinessPhoto: "",
        });
        refetchAppilcation();
        closeAddLoanGuarantor();
        toggleAddLoanGuarantorSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleSubmitGuarantor = () => {
    addGuarantorMutate();
  };

  const [guarantorChattel, setGuarantorChattel] = useState({
    name: "",
    location: "",
    value: 0,
    fileUrl: "",
    description: "",
    condition: "Good",
    images: null,
  });

  const toggleAddGuarantorChattel = (guarantor) => {
    setSelectedGuarantor(guarantor);
    setOpenAddGuarantorChattel(true);
  };
  const closeAddGuarantorChattel = () => {
    setOpenAddGuarantorChattel(false);
  };

  const {
    mutate: guarantorChattelMutate,
    isLoading: isLoadingGuarantorChattel,
  } = useMutation({
    mutationKey: ["update guarantor chattel"],
    mutationFn: () =>
      addGuarantorChattel(
        selectedGuarantor?.id,
        guarantorChattel.name,
        guarantorChattel.location,
        guarantorChattel.value,
        guarantorChattel.fileUrl,
        guarantorChattel.description,
        guarantorChattel.condition,
        guarantorChattel.images,
      ),
    onSuccess: () => {
      setGuarantorChattel({
        name: "",
        location: "",
        value: 0,
        fileUrl: "",
        description: "",
        condition: "Good",
        images: null,
      });
      refetchAppilcation();
      closeAddGuarantorChattel();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleSubmitGuarantorChattel = () => {
    guarantorChattelMutate();
  };

  const [reassignDetails, setReassignDetails] = useState({
    newOfficerName: "",
    currentOfficer: "",
    newOfficerId: "",
    reason: "",
    files: [],
  });

  const { mutate: reassignLoanOfficer, isLoading: loadingReassignment } =
    useMutation({
      mutationKey: ["reassign a loan officer"],
      mutationFn: () =>
        reassignALoanOfficer(
          loanApplication?.id,
          reassignDetails?.newOfficerId,
          reassignDetails?.reason,
          reassignDetails?.files,
        ),
      onSuccess: () => {
        handleAcceptReassignPrompt();
        refetchAppilcation();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleReassignOfficer = () => {
    reassignLoanOfficer();
  };

  const { mutate: addLoanDocumentMutate, isLoading: loadingLoanDocument } =
    useMutation({
      mutationKey: ["Add loan document"],
      mutationFn: () =>
        addLoanApplicationDocument(loanApplication?.id, loanDocuments),
      onSuccess: () => {
        refetchAppilcation();
        setLoanDocuments([]);
        closeEditAddDocument();
        toggleAllDocuments();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleAddLoanApplicationDocument = () => {
    addLoanDocumentMutate();
  };

  const [openAddNationalID, setOpenAddNationalID] = useState(false);
  const [selectedFrontIdFile, setSelectedFrontIdFile] = useState(null);
  const [selectedBackIdFile, setSelectedBackIdFile] = useState(null);
  const [idDetails, setIdDetails] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    identification: "",
    identification_type: "National ID",
    dateOfBirth: "",
    country_of_residence: "Kenya",
    citizenship: "Kenyan",
    gender: "",
    id_back_image: "",
    id_front_image: "",
  });

  const { mutate: updateIDMutate, isLoading: isLoadingBioInformation } =
    useMutation({
      mutationKey: ["update borrower"],
      mutationFn: () =>
        updateBorrower(
          loanApplication?.customer?.id,
          idDetails.firstName,
          idDetails.middleName,
          idDetails.lastName,
          idDetails.identification,
          idDetails.identification_type,
          idDetails.country_of_residence,
          idDetails.dateOfBirth,
          idDetails.citizenship,
          idDetails.gender,
          idDetails.id_back_image,
          idDetails.id_front_image,
        ),
      onSuccess: () => {
        refetchAppilcation();
        closeAddNationalId();
        toggleAddNationalIDSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleSubmitBioInformation = async () => {
    updateIDMutate();
  };

  const toggleAddNationalId = () => {
    setOpenAddNationalID(true);
  };
  const closeAddNationalId = () => {
    setOpenAddNationalID(false);
  };

  const [openAddNationalIDSuccess, setOpenAddNationalIDSuccess] =
    useState(false);

  const toggleAddNationalIDSuccess = () => {
    setOpenAddNationalIDSuccess(true);
  };
  const closeAddNationalIDSuccess = () => {
    setOpenAddNationalIDSuccess(false);
  };

  const [incomeDetails, setIncomeDetails] = useState({
    customerId: "",
    sourceOfIncome: "",
    yearsOfOperation: "",
    typeOfBusiness: "",
    businessOwnership: "",
    levelOfStability: "",
    risksAssociated: "",
    strengthOfBusiness: "",
    mpesaStatementUrl: "",
    mpesaCode: "",
    businessLicense: "",
    employerName: "",
    monthlyIncome: "",
    durationAtEmployer: "",
    kraPin: "",
    businessName: "",
    statementType: "",
    bankStatementType: "",
    bankStatementUrl: "",
    bankStatementCode: "",
    bankName: "",
    bankStatementPeriod: "",
  });
  const [payslips, setPayslips] = useState([]);
  const [openAddPayslips, setOpenAddPayslips] = useState(false);

  const toggleAddPayslips = () => {
    setOpenAddPayslips(true);
  };
  const closeAddPayslips = () => {
    setOpenAddPayslips(false);
  };

  const [openAddPayslipsSuccess, setOpenAddPayslipsSuccess] = useState(false);

  const toggleAddPayslipsSuccess = () => {
    setOpenAddPayslipsSuccess(true);
  };
  const closeAddPayslipsSuccess = () => {
    setOpenAddPayslipsSuccess(false);
  };

  const { mutate: addIncomePayslips, isLoading: loadingPayslips } = useMutation(
    {
      mutationKey: ["add payslips"],
      mutationFn: () => addPayslips(loanApplication?.customer?.id, payslips),
      onSuccess: () => {
        closeAddPayslips();
        toggleAddPayslipsSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    },
  );

  const handleAddPayslips = () => {
    addIncomePayslips();
  };

  const [openAddBankStatement, setOpenAddBankStatement] = useState(false);
  const toggleAddBankStatement = () => {
    setOpenAddBankStatement(true);
  };
  const closeAddBankStatement = () => {
    setOpenAddBankStatement(false);
  };

  const [openAddBankStatementSuccess, setOpenAddBankStatementSuccess] =
    useState(false);
  const toggleAddBankStatementSuccess = () => {
    setOpenAddBankStatementSuccess(true);
  };
  const closeAddBankStatementSuccess = () => {
    setOpenAddBankStatementSuccess(false);
  };

  const { mutate: addMyBankStatement, isLoading: loadingBankStatement } =
    useMutation({
      mutationKey: ["add bank statement"],
      mutationFn: () =>
        addBankStatement(
          loanApplication?.customer?.id,
          incomeDetails?.bankName,
          incomeDetails?.bankStatementUrl,
          incomeDetails?.bankStatementCode,
          incomeDetails?.bankStatementPeriod,
        ),
      onSuccess: () => {
        closeAddBankStatement();
        toggleAddBankStatementSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleAddBankStatement = () => {
    addMyBankStatement();
  };

  const { mutate: addMyMpesaStatement, isLoading: loadingMpesaStatement } =
    useMutation({
      mutationKey: ["add mpesa statement"],
      mutationFn: () =>
        addMpesaStatement(
          loanApplication?.customer?.id,
          incomeDetails?.mpesaCode,
          incomeDetails?.mpesaStatementUrl,
        ),
      onSuccess: () => {
        closeAddMpesaStatement();
        toggleAddMpesaStatementSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const [openAddMpesaStatement, setOpenAddMpesaStatement] = useState(false);
  const toggleAddMpesaStatement = () => {
    setOpenAddMpesaStatement(true);
  };
  const closeAddMpesaStatement = () => {
    setOpenAddMpesaStatement(false);
  };

  const [openAddMpesaStatementSuccess, setOpenAddMpesaStatementSuccess] =
    useState(false);
  const toggleAddMpesaStatementSuccess = () => {
    setOpenAddMpesaStatementSuccess(true);
  };
  const closeAddMpesaStatementSuccess = () => {
    setOpenAddMpesaStatementSuccess(false);
  };

  const handleAddMpesaStatement = () => {
    addMyMpesaStatement();
  };

  const [openLoanChattels, setOpenLoanChattels] = useState(false);
  const [openAddLoanChattel, setOpenAddLoanChattel] = useState(false);
  const [borrowerChattel, setBorrowerChattel] = useState({
    name: "",
    location: "",
    value: 0,
    fileUrl: "",
    description: "",
    condition: "Good",
    images: null,
  });
  const toggleLoanChattels = () => {
    setOpenLoanChattels(true);
  };
  const closeLoanChattels = () => {
    setOpenLoanChattels(false);
  };

  const handleCollectChattel = () => {
    closeLoanChattels();
    toggleAddLoanChattel();
  };

  const toggleAddLoanChattel = () => {
    setOpenAddLoanChattel(true);
  };
  const closeAddLoanChattel = () => {
    setOpenAddLoanChattel(false);
  };

  const { mutate: updateChattelMutate, isLoading: isLoadingChattel } =
    useMutation({
      mutationKey: ["update chattel"],
      mutationFn: () =>
        addBorrowerChattel(
          loanApplication?.customer?.id,
          borrowerChattel.name,
          borrowerChattel.location,
          borrowerChattel.value,
          borrowerChattel.fileUrl,
          borrowerChattel.description,
          borrowerChattel.condition,
          borrowerChattel.images,
        ),
      onSuccess: () => {
        setBorrowerChattel({
          name: "",
          location: "",
          value: 0,
          fileUrl: "",
          description: "",
          condition: "Good",
          images: null,
        });
        refetchAppilcation();
        closeAddLoanChattel();
        toggleLoanChattels();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleCreateLoanChattel = () => {
    updateChattelMutate();
  };

  const [openEditLoanDetailsSuccess, setOpenEditLoanDetailsSuccess] =
    useState(false);
  const [loanEditDetails, setLoanEditDetails] = useState({
    loanAmount: "",
    loanPeriod: "",
    loanPurpose: "",
  });

  const toggleAllDocuments = () => {
    setOpenAllDocuments(true);
  };
  const closeAllDocuments = () => {
    refetchAppilcation();
    setOpenAllDocuments(false);
  };

  const handleOpenAddDocument = () => {
    closeAllDocuments();
    toggleEditAddDocument();
  };

  const toggleEditAddDocument = () => {
    setOpenEditAddDocument(true);
  };
  const closeEditAddDocument = () => {
    setOpenEditAddDocument(false);
  };

  const toggleEditLoanDetails = () => {
    setLoanEditDetails({
      loanAmount: loanApplication?.loanAmount,
      loanPeriod: loanApplication?.loanPeriod,
      loanPurpose: loanApplication?.loanPurpose,
    });
    setOpenEditLoanDetails(true);
  };
  const closeEditLoanDetails = () => {
    setOpenEditLoanDetails(false);
  };

  const handleUpdateLoanDetails = () => {
    closeEditLoanDetails();
    toggleEditLoanDetailsSuccess();
  };

  const toggleEditLoanDetailsSuccess = () => {
    setOpenEditLoanDetailsSuccess(true);
  };
  const closeEditLoanDetailsSuccess = () => {
    setOpenEditLoanDetailsSuccess(false);
  };

  const [guarantorEditDetails, setGuarantorEditDetails] = useState({
    guarantorName: "",
    guarantorPhone: "",
    guarantorEmail: "",
    residenceCounty: "",
    residenceSubCounty: "",
    residenceWard: "",
    residenceVillage: "",
    residenceLatitude: "",
    residenceLongitude: "",
    signatureUrl: "",
    guaranteeAmount: "",
    status: "",
    reason: "",
    residenceLocation: "",
    guarantorPhoto: "",
    guarantorBusinessPhoto: "",
  });
  const [selectedChattel, setSelectedChattel] = useState({});
  const [selectedBorrowerChattel, setSelectedBorrowerChattel] = useState({});
  const [selectedGuarantor, setSelectedGuarantor] = useState({});
  const [guarantorChattelEditDetails, setGuarantorChattelEditDetails] =
    useState({
      name: "",
      location: "",
      value: 0,
      fileUrl: "",
      description: "",
      condition: "Good",
      images: null,
    });
  const [openAddSelfie, setOpenAddSelfie] = useState(false);
  const [openAddSelfieSuccess, setOpenAddSelfieSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const { mutate: uploadSelfie, isLoading: loadingSelfie } = useMutation({
    mutationKey: ["upload selfie"],
    mutationFn: () =>
      uploadBorrowerSelfie(selectedFile, loanApplication?.customer?.id),
    onSuccess: () => {
      refetchAppilcation();
      closeAddSelfie();
      toggleAddSelfieSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleUploadSelfie = () => {
    uploadSelfie();
  };

  const toggleAddSelfie = () => {
    setOpenAddSelfie(true);
  };
  const closeAddSelfie = () => {
    setOpenAddSelfie(false);
  };

  const toggleAddSelfieSuccess = () => {
    setOpenAddSelfieSuccess(true);
  };
  const closeAddSelfieSuccess = () => {
    setOpenAddSelfieSuccess(false);
  };

  const toggleEditGuarantorSuccess = () => {
    setOpenEditGuarantorSuccess(true);
  };
  const closeEditGuarantorSuccess = () => {
    setOpenEditGuarantorSuccess(false);
  };

  const toggleEditChattelSuccess = () => {
    setOpenEditChattelSuccess(true);
  };
  const closeEditChattelSuccess = () => {
    setOpenEditChattelSuccess(false);
  };

  const [borrowerChattelEditDetails, setBorrowerChattelEditDetails] = useState({
    name: "",
    location: "",
    value: 0,
    fileUrl: "",
    description: "",
    condition: "Good",
    images: null,
  });

  const {
    mutate: editBorrowerChattelMutate,
    isLoading: loadingEditBorrowerChattel,
  } = useMutation({
    mutationKey: ["edit borrower chattel"],
    mutationFn: () =>
      editMyBorrowerChattel(
        selectedBorrowerChattel?.id,
        borrowerChattelEditDetails?.name,
        borrowerChattelEditDetails?.location,
        borrowerChattelEditDetails?.value,
        borrowerChattelEditDetails?.fileUrl,
        borrowerChattelEditDetails?.description,
        borrowerChattelEditDetails?.condition,
        borrowerChattelEditDetails?.images,
      ),
    onSuccess: () => {
      refetchAppilcation();
      closeEditChattel();
      toggleEditChattelSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleEditChattel = () => {
    editBorrowerChattelMutate();
  };

  const toggleEditGuarantor = (guarantor) => {
    setSelectedGuarantor(guarantor);
    setGuarantorEditDetails({
      guarantorName: guarantor?.guarantorName,
      guaranteeAmount: guarantor?.guaranteeAmount,
      residenceLocation: guarantor?.residenceLocation,
      guarantorEmail: guarantor?.guarantorEmail,
      guarantorPhone: guarantor?.guarantorPhone,
      reason: guarantor?.reason,
      guarantorPhoto: guarantor?.guarantorPhoto,
      guarantorBusinessPhoto: guarantor?.guarantorBusinessPhoto,
    });
    setOpenEditGuarantor(true);
  };
  const closeEditGuarantor = () => {
    setOpenEditGuarantor(false);
  };

  const { mutate: updateGuarantorMutate, isLoading: loadingEditGuarantor } =
    useMutation({
      mutationKey: ["edit guarantor"],
      mutationFn: () =>
        updateApplicationGuarantor(
          selectedGuarantor?.id,
          guarantorEditDetails.guarantorName,
          guarantorEditDetails.guarantorPhone,
          guarantorEditDetails.guarantorEmail,
          guarantorEditDetails.residenceCounty,
          guarantorEditDetails.residenceSubCounty,
          guarantorEditDetails.residenceWard,
          guarantorEditDetails.residenceVillage,
          guarantorEditDetails.residenceLatitude,
          guarantorEditDetails.residenceLongitude,
          guarantorEditDetails.signatureUrl,
          guarantorEditDetails.guaranteeAmount,
          guarantorEditDetails.reason,
          guarantorEditDetails?.guarantorPhoto,
          guarantorEditDetails?.guarantorBusinessPhoto,
          guarantorEditDetails?.residenceLocation,
        ),
      onSuccess: () => {
        refetchAppilcation();
        closeEditGuarantor();
        toggleEditGuarantorSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const {
    mutate: updateGuarantorLocationMutate,
    isLoading: loadingEditGuarantorLocation,
  } = useMutation({
    mutationKey: ["edit guarantor location"],
    mutationFn: () =>
      updateApplicationGuarantorLocation(
        selectedGuarantor?.id,
        guarantorEditDetails.residenceLatitude,
        guarantorEditDetails.residenceLongitude,
        guarantorEditDetails?.residenceLocation,
      ),
    onSuccess: () => {
      refetchAppilcation();
      closeEditGuarantorLocation();
      toggleEditGuarantorLocationSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleEditGuarantor = () => {
    updateGuarantorMutate();
  };

  const handleUpdateGuarantorLocation = () => {
    updateGuarantorLocationMutate();
  };

  const toggleEditGuarantorChattel = (chattel) => {
    setSelectedChattel(chattel);
    setGuarantorChattelEditDetails({
      name: chattel?.name,
      location: chattel?.location,
      value: chattel?.value,
      description: chattel?.description,
      condition: chattel?.condition,
    });
    setOpenEditGuarantorChattel(true);
  };

  const {
    mutate: editGuarantorChattelMutate,
    isLoading: loadingEditGuarantorChattel,
  } = useMutation({
    mutationKey: ["edit guarantor chattel"],
    mutationFn: () =>
      editMyBorrowerChattel(
        selectedChattel?.id,
        guarantorChattelEditDetails?.name,
        guarantorChattelEditDetails?.location,
        guarantorChattelEditDetails?.value,
        guarantorChattelEditDetails?.fileUrl,
        guarantorChattelEditDetails?.description,
        guarantorChattelEditDetails?.condition,
        guarantorChattelEditDetails?.images,
      ),
    onSuccess: () => {
      refetchAppilcation();
      closeEditGuarantorChattel();
      toggleEditGuarantorChattelSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const closeEditGuarantorChattel = () => {
    setOpenEditGuarantorChattel(false);
  };

  const handleEditGuarantorChattel = () => {
    editGuarantorChattelMutate();
  };

  const toggleEditGuarantorChattelSuccess = () => {
    setOpenEditGuarantorChattelSuccess(true);
  };
  const closeEditGuarantorChattelSuccess = () => {
    setOpenEditGuarantorChattelSuccess(false);
  };

  const toggleReassignOfficer = () => {
    setReassignDetails((prev) => ({
      ...prev,
      currentOfficer: `${loanApplication?.loanOfficer?.firstname} ${loanApplication?.loanOfficer?.lastname}`,
    }));
    setOpenReassignOfficer(true);
  };
  const closeReassignOfficer = () => {
    setOpenReassignOfficer(false);
  };
  const toggleEditChattel = (chattel) => {
    setSelectedBorrowerChattel(chattel);
    setBorrowerChattelEditDetails({
      name: chattel?.name,
      location: chattel?.location,
      value: chattel?.value,
      description: chattel?.description,
      condition: chattel?.condition,
    });
    setOpenEditChattel(true);
  };
  const closeEditChattel = () => {
    setOpenEditChattel(false);
  };

  const handleCollectReassignDetails = () => {
    closeReassignOfficer();
    toggleReassignPrompt();
  };

  const toggleReassignPrompt = () => {
    setOpenReassignPrompt(true);
  };
  const closeReassignPrompt = () => {
    setOpenReassignPrompt(false);
  };

  const handleAcceptReassignPrompt = () => {
    closeReassignPrompt();
    toggleReassignSuccess();
  };

  const toggleReassignSuccess = () => {
    setOpenReassignSuccess(true);
  };
  const closeReassignSuccess = () => {
    setOpenReassignSuccess(false);
  };

  const [openEditPersonalDetailsSuccess, setOpenEditPersonalDetailsSuccess] =
    useState(false);

  const toggleEditPersonalDetailsSuccess = () => {
    setOpenEditPersonalDetailsSuccess(true);
  };
  const closeEditPersonalDetailsSuccess = () => {
    setOpenEditPersonalDetailsSuccess(false);
  };

  const {
    mutate: editPersonalDetailsMutate,
    isLoading: isLoadingPersonalDetails,
  } = useMutation({
    mutationKey: ["edit personal details"],
    mutationFn: () =>
      editBorrowerPersonalDetails(
        loanApplication?.customer?.id,
        borrowerEditDetails?.firstname,
        borrowerEditDetails?.middlename,
        borrowerEditDetails?.lastname,
        borrowerEditDetails?.identification,
        borrowerEditDetails?.dob,
        borrowerEditDetails?.gender,
        borrowerEditDetails?.mobileno,
        borrowerEditDetails?.altPhoneNumber,
        borrowerEditDetails?.dependants,
        borrowerEditDetails?.maritalStatus,
        borrowerEditDetails?.email,
        borrowerEditDetails?.postal_address,
      ),
    onSuccess: () => {
      refetchAppilcation();
      closeEditPersonalDetails();
      toggleEditPersonalDetailsSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleEditPersonalDetails = async () => {
    await editPersonalDetailsMutate();
  };

  const [mySelectedChattel, setMySelectedChattel] = useState({});
  const [openChattel, setOpenChattel] = useState(false);

  const toggleChattel = (chattel) => {
    setMySelectedChattel(chattel);
    setOpenChattel(true);
  };
  const closeChattel = () => {
    setOpenChattel(false);
  };

  const [openEditKinDetails, setOpenEditKinDetails] = useState(false);
  const [openEditKinSuccess, setOpenEditKinSuccess] = useState(false);
  const [kinEditDetails, setKinEditDetails] = useState({
    name: "",
    phoneNumber: "",
    location: "",
    relationship: "",
    dateOfBirth: "",
  });

  const toggleEditKinSuccess = () => {
    setOpenEditKinSuccess(true);
  };
  const closeEditKinSuccess = () => {
    setOpenEditKinSuccess(false);
  };

  const { mutate: updateKinMutate, isLoading: loadingUpdateKin } = useMutation({
    mutationKey: ["edit kin details"],
    mutationFn: () =>
      editNextOfKinDetails(
        loanApplication?.customer?.nextOfKins?.[0]?.id,
        kinEditDetails?.name,
        kinEditDetails?.dateOfBirth,
        kinEditDetails?.relationship,
        kinEditDetails?.phoneNumber,
        kinEditDetails?.location,
      ),
    onSuccess: () => {
      refetchAppilcation();
      closeEditKinDetails();
      toggleEditKinSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleEditNextOfKinDetails = () => {
    updateKinMutate();
  };

  const [openEditIncomeDetails, setOpenEditIncomeDetails] = useState(false);
  const [openEditIncomeSuccess, setOpenEditIncomeSuccess] = useState(false);
  const [openEditBusinessLocation, setOpenEditBusinessLocation] =
    useState(false);
  const [openEditBusinessLocationSuccess, setOpenEditBusinessLocationSuccess] =
    useState(false);
  const [
    openEditResidenceLocationSuccess,
    setOpenEditResidenceLocationSuccess,
  ] = useState(false);
  const [openEditResidenceLocation, setOpenEditResidenceLocation] =
    useState(false);
  const [incomeEditDetails, setIncomeEditDetails] = useState({
    sourceOfIncome: "",
    yearsOfOperation: "",
    typeOfBusiness: "",
    businessOwnership: "",
    levelOfStability: "",
    risksAssociated: "",
    strengthOfBusiness: "",
    businessLicense: "",
    employerName: "",
    monthlyIncome: "",
    durationAtEmployer: "",
    businessKraPin: "",
    businessName: "",
  });
  const [editBusinessLocationDetails, setEditBusinessLocationDetails] =
    useState({
      businessName: "",
      locationLink: "",
      latitude: "",
      longitude: "",
      physicalAddress: "",
      locationDescription: "",
      businessImageUrl: "",
      businessPostalAddress: "",
    });

  const toggleEditBusinessLocationSuccess = () => {
    setOpenEditBusinessLocationSuccess(true);
  };
  const closeEditBusinessLocationSuccess = () => {
    setOpenEditBusinessLocationSuccess(false);
  };

  const toggleEditResidenceLocationSuccess = () => {
    setOpenEditResidenceLocationSuccess(true);
  };
  const closeEditResidenceLocationSuccess = () => {
    setOpenEditResidenceLocationSuccess(false);
  };

  const { mutate: editBusinessMutate, isLoading: loadingEditBusiness } =
    useMutation({
      mutationKey: ["edit business location details"],
      mutationFn: () =>
        editMyBorrowerBusinessDetails(
          loanApplication?.customer?.id,
          editBusinessLocationDetails?.businessName,
          editBusinessLocationDetails?.latitude,
          editBusinessLocationDetails?.longitude,
          editBusinessLocationDetails?.physicalAddress,
          editBusinessLocationDetails?.locationDescription,
          editBusinessLocationDetails?.businessImageUrl,
          editBusinessLocationDetails?.businessPostalAddress,
        ),
      onSuccess: () => {
        refetchAppilcation();
        closeEditBusinessLocation();
        toggleEditBusinessLocationSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const [editResidenceLocationDetails, setEditResidenceLocationDetails] =
    useState({
      locationLink: "",
      latitude: "",
      longitude: "",
      physicalAddress: "",
      locationDescription: "",
      residenceImageUrl: "",
    });

  const toggleEditBusinessLocation = () => {
    const business = loanApplication?.customer?.businesses?.[0] ?? {};
    setEditBusinessLocationDetails({
      businessName: business?.businessName,
      physicalAddress: business?.businessPhysicalAddress,
      locationDescription: business?.businessLocationDescription,
      businessImageUrl: business?.businessImageUrl,
    });
    setOpenEditBusinessLocation(true);
  };

  const closeEditBusinessLocation = () => {
    setOpenEditBusinessLocation(false);
  };

  const { mutate: editResidenceMutate, isLoading: loadingEditResidence } =
    useMutation({
      mutationKey: ["edit residence location details"],
      mutationFn: () =>
        patchBorrowerResidenceDetails(
          loanApplication?.customer?.id,
          editResidenceLocationDetails?.latitude,
          editResidenceLocationDetails?.longitude,
          editResidenceLocationDetails?.physicalAddress,
          editResidenceLocationDetails?.locationDescription,
          editResidenceLocationDetails?.residenceImageUrl,
        ),
      onSuccess: () => {
        refetchAppilcation();
        closeEditResidenceLocation();
        toggleEditResidenceLocationSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleEditBusinessLocation = () => {
    editBusinessMutate();
  };
  const handleEditResidenceLocation = () => {
    editResidenceMutate();
  };

  const toggleEditResidenceLocation = () => {
    const residence = loanApplication?.customer?.residences?.[0] ?? {};
    setEditResidenceLocationDetails({
      physicalAddress: residence?.residencePhysicalAddress,
      locationDescription: residence?.residenceLocationDescription,
      residenceImageUrl: residence?.residenceImageUrl,
    });
    setOpenEditResidenceLocation(true);
  };

  const closeEditResidenceLocation = () => {
    setOpenEditResidenceLocation(false);
  };

  const toggleEditIncomeSuccess = () => {
    setOpenEditIncomeSuccess(true);
  };

  const closeEditIncomeSuccess = () => {
    setOpenEditIncomeSuccess(false);
  };

  const { mutate: editIncomeMutate, isLoading: loadingEditIncome } =
    useMutation({
      mutationKey: ["edit customer income"],
      mutationFn: () =>
        editIncomeDetails(
          loanApplication?.customer?.id,
          incomeEditDetails?.sourceOfIncome,
          incomeEditDetails?.yearsOfOperation,
          incomeEditDetails?.typeOfBusiness,
          incomeEditDetails?.businessOwnership,
          incomeEditDetails?.levelOfStability,
          incomeEditDetails?.risksAssociated,
          incomeEditDetails?.strengthOfBusiness,
          incomeEditDetails?.businessLicense,
          incomeEditDetails?.employerName,
          incomeEditDetails?.monthlyIncome,
          incomeEditDetails?.durationAtEmployer,
          incomeEditDetails?.businessKraPin,
          incomeEditDetails?.businessName,
        ),
      onSuccess: () => {
        refetchAppilcation();
        closeEditIncomeDetails();
        toggleEditIncomeSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const handleEditIncomeDetails = () => {
    editIncomeMutate();
  };

  const toggleEditIncomeDetails = () => {
    const income = loanApplication?.customer?.incomes?.[0] ?? {};
    setIncomeEditDetails({
      sourceOfIncome: income.sourceOfIncome || "",
      yearsOfOperation: income.yearsOfOperation || "",
      typeOfBusiness: income.typeOfBusiness || "",
      businessOwnership: income.businessOwnership || "",
      levelOfStability: income.levelOfStability || "",
      risksAssociated: income.risksAssociated || "",
      strengthOfBusiness: income.strengthOfBusiness || "",
      businessLicense: income.businessLicense || "",
      employerName: income.employerName || "",
      monthlyIncome: income.monthlyIncome || "",
      durationAtEmployer: income.durationAtEmployer || "",
      businessKraPin: income.businessKraPin || "",
      businessName: income.businessName || "",
    });
    setOpenEditIncomeDetails(true);
  };

  const closeEditIncomeDetails = () => {
    setOpenEditIncomeDetails(false);
  };

  const toggleEditKinDetails = () => {
    setKinEditDetails((prev) => ({
      ...prev,
      name: loanApplication?.customer?.nextOfKins?.[0]?.name,
      phoneNumber: loanApplication?.customer?.nextOfKins?.[0]?.phoneNumber,
      location: loanApplication?.customer?.nextOfKins?.[0]?.location,
      relationship: loanApplication?.customer?.nextOfKins?.[0]?.relationship,
      dateOfBirth: loanApplication?.customer?.nextOfKins?.[0]?.dateOfBirth,
    }));
    setOpenEditKinDetails(true);
  };

  const closeEditKinDetails = () => {
    setOpenEditKinDetails(false);
  };

  const toggleEditPersonalDetails = () => {
    setBorrowerEditDetails((prev) => ({
      ...prev,
      firstname: loanApplication?.customer?.firstname,
      middlename: loanApplication?.customer?.middlename,
      lastname: loanApplication?.customer?.lastname,
      identification: loanApplication?.customer?.identification,
      dob: loanApplication?.customer?.dob,
      email: loanApplication?.customer?.email,
      mobileno: loanApplication?.customer?.mobileno,
      altPhoneNumber: loanApplication?.customer?.altPhoneNumber,
      gender: loanApplication?.customer?.gender?.toLocaleLowerCase(),
      postal_address: loanApplication?.customer?.postal_address,
      dependants: loanApplication?.customer?.dependants,
      maritalStatus: loanApplication?.customer?.maritalStatus,
    }));
    setOpenEditPersonalDetails(true);
  };
  const closeEditPersonalDetails = () => {
    setOpenEditPersonalDetails(false);
  };

  const toggleOpenPromptApproval = () => {
    const status = loanApplication?.status?.toString().trim().toLowerCase();
    if (status === "approved") {
      showError("Loan already approved");
      return;
    }

    if (status === "disbursed") {
      showError("Loan already disbursed");
      return;
    }

    if (status === "declined") {
      showError("Loan application declined");
      return;
    }
    setOpenPromptApproval(true);
  };

  const closeOpenPromptApproval = () => {
    setOpenPromptApproval(false);
  };

  const handleContinueApproval = () => {
    closeOpenPromptApproval();
    toggleLoanDetails();
  };

  const toggleLoanDetails = () => {
    setOpenLoanDetails(true);
  };
  const closeLoanDetails = () => {
    setOpenLoanDetails(false);
  };

  const handleContinueChecklist = () => {
    closeLoanDetails();
    toggleChecklist();
  };

  const toggleChecklist = () => {
    setOpenChecklist(true);
  };
  const closeChecklist = () => {
    setOpenChecklist(false);
  };

  const toggleDeclineChecklist = () => {
    setOpenDeclineChecklist(true);
  };
  const closeDeclineChecklist = () => {
    setOpenDeclineChecklist(false);
  };

  const handleContinueApproveAmount = () => {
    setApprovalDetails((prev) => ({
      ...prev,
      approvalAmount: loanApplication?.loanAmount ?? "",
    }));
    closeChecklist();
    toggleApprovedAmount();
  };

  const toggleApprovedAmount = () => {
    setOpenApprovedAmount(true);
  };
  const closeApprovedAmount = () => {
    setOpenApprovedAmount(false);
  };

  const handleFinalizeApproval = () => {
    closeApprovedAmount();
    toggleFinalizeApproval();
  };

  const toggleFinalizeApproval = () => {
    setOpenFinalizeApproval(true);
  };
  const closeFinalizeApproval = () => {
    setOpenFinalizeApproval(false);
  };

  const handleApproveLoan = async () => {
    await approvalMutate();
  };

  const toggleApproveSuccess = () => {
    setOpenApproveSuccess(true);
  };
  const closeApproveSuccess = () => {
    setOpenApproveSuccess(false);
  };

  const handleCompleteDeclineChecklist = () => {
    closeLoanDetails();
    toggleDeclineChecklist();
  };

  const handleBackToLoanDetailsFromDeclineChecklist = () => {
    closeDeclineChecklist();
    toggleLoanDetails();
  };

  const handleFinalizeDecline = () => {
    closeDeclineChecklist();
    toggleFinalizeDecline();
  };

  const toggleFinalizeDecline = () => {
    setOpenFinalizeDecline(true);
  };
  const closeFinalizeDecline = () => {
    setOpenFinalizeDecline(false);
  };

  const toggleDeclineSuccess = () => {
    setOpenDeclineSuccess(true);
  };
  const closeDeclineSuccess = () => {
    setOpenDeclineSuccess(false);
  };

  const handleDeclineLoan = async () => {
    await declineMutate();
  };

  const handleBackToLoanDetailsFromChecklist = () => {
    closeChecklist();
    toggleLoanDetails();
  };

  const handleBackToAmountFromFinalizeApproval = () => {
    closeFinalizeApproval();
    toggleApprovedAmount();
  };

  const handleBackLoanDetailsFromFinalizeDecline = () => {
    closeFinalizeDecline();
    toggleDeclineChecklist();
  };

  const toggleGuarantor = (index) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
  };

  const tabClass = (tab) =>
    `w-[24%] cursor-pointer flex items-center justify-center h-full ${
      activeTab === tab
        ? "border-b-[5px] border-[#000000] bg-[#FFDEDE] text-black font-medium"
        : "text-[#5C5C5C] bg-white text-[16px] font-normal"
    }`;

  const {
    data: loanApplication,
    refetch: refetchAppilcation,
    isFetching,
  } = useQuery({
    queryKey: ["application", id],
    queryFn: () => getLoanApplicationById(id),
    enabled: !!id,
  });

  const { refetch: refetchApprovals } = useQuery({
    queryKey: ["approvals", id],
    queryFn: async () => {
      const response = await getLoanApplicationApprovals(id);
      return response.data.data;
    },
    initialData: [],
    enabled: !!id,
    onSuccess: (data) => {
      setApprovals(data.stages);
      setGeneralStatus(data?.generalStatus);
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const { mutate: approvalMutate, isLoading: approvalLoading } = useMutation({
    mutationKey: ["approve application"],
    mutationFn: () =>
      approveApplication(
        id,
        auth?.user?.id,
        approvalDetails?.checklist,
        "approved",
        auth?.user?.role?.name === "Loan Officer"
          ? "peer"
          : auth?.user?.role?.name === "Manager"
            ? "manager"
            : auth?.user?.role?.name === "Super Admin"
              ? "final"
              : "peer",
        approvalDetails.description,
        approvalDetails.additionalDocuments,
      ),
    onSuccess: () => {
      closeFinalizeApproval();
      toggleApproveSuccess();
      refetchAppilcation();
      refetchApprovals();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const { mutate: declineMutate, isLoading: declineLoading } = useMutation({
    mutationKey: ["decline application"],
    mutationFn: () =>
      approveApplication(
        id,
        auth?.user?.id,
        approvalDetails?.checklist,
        "declined",
        auth?.user?.role?.name === "Loan Officer"
          ? "peer"
          : auth?.user?.role?.name === "Manager"
            ? "manager"
            : auth?.user?.role?.name === "Super Admin"
              ? "final"
              : "peer",
        approvalDetails.description,
        approvalDetails.additionalDocuments,
      ),
    onSuccess: () => {
      refetchAppilcation();
      closeFinalizeDecline();
      toggleDeclineSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const { mutate: deleteDocument } = useMutation({
    mutationKey: ["delete document"],
    mutationFn: (id) => deleteLoanApplicationDocument(loanApplication?.id, id),
    onSuccess: () => {
      refetchAppilcation();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const [guarantorDocuments, setGuarantorDocuments] = useState([]);
  const [openGuarantorDocuments, setOpenGuarantorDocuments] = useState(false);
  const [openAddGuarantorDocument, setOpenAddGuarantorDocument] =
    useState(false);

  const toggleGuarantorDocuments = (guarantor) => {
    setSelectedGuarantor(guarantor);
    setOpenGuarantorDocuments(true);
  };
  const closeGuarantorDocuments = () => {
    setOpenGuarantorDocuments(false);
  };

  const toggleAddGuarantorDocument = () => {
    setOpenAddGuarantorDocument(true);
  };
  const closeAddGuarantorDocument = () => {
    setOpenAddGuarantorDocument(false);
  };

  const handleAddGuarantorDocument = () => {
    closeGuarantorDocuments();
    toggleAddGuarantorDocument();
  };

  const { data: allGuarantorDocuments, refetch: refetchGuarantorDocuments } =
    useQuery({
      queryKey: ["guarantor documents", selectedGuarantor?.id],
      queryFn: () => getGuarantorDocuments(selectedGuarantor?.id),
      initialData: [],
      enabled: !!selectedGuarantor?.id,
    });

  const {
    mutate: addGuarantorDocumentMutate,
    isLoading: loadingGuarantorDocument,
  } = useMutation({
    mutationKey: ["Add guarantor document"],
    mutationFn: () =>
      addLoanGuarantorDocument(selectedGuarantor?.id, guarantorDocuments),
    onSuccess: () => {
      refetchAppilcation();
      refetchGuarantorDocuments();
      setGuarantorDocuments([]);
      closeAddGuarantorDocument();
      toggleGuarantorDocuments();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleSubmitGuarantorDocument = () => {
    addGuarantorDocumentMutate();
  };

  const handleDeleteDocument = (document) => {
    deleteDocument(document?.id);
  };

  const formatValue = (val) =>
    val && val.toString().trim() !== "" ? val : "N/A";

  const [viewUrl, setViewUrl] = useState(null);

  const handleViewDocument = (url) => {
    setViewUrl(url);
  };

  return (
    <>
      {openAddGuarantorDocument && (
        <AddGuarantorDocument
          closeAddGuarantorDocument={closeAddGuarantorDocument}
          setLoanDocuments={setGuarantorDocuments}
          handleSubmitLoanDocument={handleSubmitGuarantorDocument}
          loadingLoanDocument={loadingGuarantorDocument}
        />
      )}
      {openGuarantorDocuments && (
        <GuarantorDocuments
          closeGuarantorDocuments={closeGuarantorDocuments}
          guarantor={selectedGuarantor}
          documents={allGuarantorDocuments}
          handleAddGuarantorDocument={handleAddGuarantorDocument}
        />
      )}
      {openAddSelfieSuccess && (
        <AddSelfieSuccess closeAddSelfieSuccess={closeAddSelfieSuccess} />
      )}
      {openAddSelfie && (
        <UploadSelfie
          closeUploadSelfie={closeAddSelfie}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          handleUploadFile={handleUploadSelfie}
          loading={loadingSelfie}
        />
      )}
      {openAddPayslipsSuccess && (
        <AddPayslipsSuccess closeAddPayslipsSuccess={closeAddPayslipsSuccess} />
      )}
      {openAddMpesaStatementSuccess && (
        <AddMpesaStatementSuccess
          closeAddMpesaStatementSuccess={closeAddMpesaStatementSuccess}
        />
      )}
      {openAddBankStatementSuccess && (
        <AddBankStatementSuccess
          closeAddBankStatementSuccess={closeAddBankStatementSuccess}
        />
      )}
      {openAddBankStatement && (
        <AddBankStatement
          closeAddBankStatement={closeAddBankStatement}
          incomeDetails={incomeDetails}
          setIncomeDetails={setIncomeDetails}
          loading={loadingBankStatement}
          handleNextStep={handleAddBankStatement}
        />
      )}
      {openAddMpesaStatement && (
        <AddMpesaStatement
          closeAddMpesaStatement={closeAddMpesaStatement}
          incomeDetails={incomeDetails}
          setIncomeDetails={setIncomeDetails}
          loading={loadingMpesaStatement}
          handleNextStep={handleAddMpesaStatement}
        />
      )}
      {openAddPayslips && (
        <AddPayslips
          closeAddPayslips={closeAddPayslips}
          setPayslips={setPayslips}
          loading={loadingPayslips}
          handleNextStep={handleAddPayslips}
        />
      )}
      {openAddNationalIDSuccess && (
        <AddNationalIdSuccess
          closeAddNationalIdSuccess={closeAddNationalIDSuccess}
        />
      )}
      {openAddNationalID && (
        <AddNationalId
          closeAddNationalId={closeAddNationalId}
          handleNextStep={handleSubmitBioInformation}
          idDetails={idDetails}
          setIdDetails={setIdDetails}
          selectedFrontIdFile={selectedFrontIdFile}
          setSelectedFrontIdFile={setSelectedFrontIdFile}
          selectedBackIdFile={selectedBackIdFile}
          setSelectedBackIdFile={setSelectedBackIdFile}
          loading={isLoadingBioInformation}
        />
      )}
      {openChattel && (
        <Chattel closeChattel={closeChattel} chattel={mySelectedChattel} />
      )}
      {openAddLoanGuarantorSuccess && (
        <AddLoanGuarantorSuccess
          closeAddLoanGuarantorSuccess={closeAddLoanGuarantorSuccess}
        />
      )}
      {openAddLoanGuarantor && (
        <AddLoanGuarantor
          closeAddGuarantor={closeAddLoanGuarantor}
          handleAddMyGuarantor={handleSubmitGuarantor}
          guarantor={myGuarantor}
          setGuarantor={setMyGuarantor}
          loading={isLoadingGuarantor}
        />
      )}
      {openEditGuarantorLocationSuccess && (
        <EditGuarantorLocationSuccess
          closeEditGuarantorLocationSuccess={closeEditGuarantorLocationSuccess}
        />
      )}
      {openEditGuarantorLocation && (
        <EditGuarantorLocation
          closeEditGuarantorLocation={closeEditGuarantorLocation}
          handleEditGuarantorLocation={handleUpdateGuarantorLocation}
          loading={loadingEditGuarantorLocation}
          guarantorEditDetails={guarantorEditDetails}
          setGuarantorEditDetails={setGuarantorEditDetails}
        />
      )}
      {openAddGuarantorChattel && (
        <AddLoanGuarantorChattel
          closeAddLoanGuarantorChattel={closeAddGuarantorChattel}
          handleAddMyGuarantorChattel={handleSubmitGuarantorChattel}
          loading={isLoadingGuarantorChattel}
          guarantorChattel={guarantorChattel}
          setGuarantorChattel={setGuarantorChattel}
        />
      )}
      {openAddLoanChattel && (
        <AddLoanChattel
          closeAddLoanChattel={closeAddLoanChattel}
          loading={isLoadingChattel}
          borrowerChattel={borrowerChattel}
          setBorrowerChattel={setBorrowerChattel}
          handleAddMyChattel={handleCreateLoanChattel}
        />
      )}
      {openLoanChattels && (
        <LoanChattels
          closeLoanChattels={closeLoanChattels}
          borrower={loanApplication?.customer}
          handleAddChattel={handleCollectChattel}
          refetchApplication={refetchAppilcation}
        />
      )}
      {openEditAddDocument && (
        <EditAddDocument
          closeEditAddDocument={closeEditAddDocument}
          setLoanDocuments={setLoanDocuments}
          loading={loadingLoanDocument}
          handleAddLoanApplicationDocument={handleAddLoanApplicationDocument}
        />
      )}
      {openAllDocuments && (
        <AllDocuments
          closeAllDocuments={closeAllDocuments}
          loanApplication={loanApplication}
          handleOpenAddDocument={handleOpenAddDocument}
          refetchApplication={refetchAppilcation}
        />
      )}
      {openEditLoanDetailsSuccess && (
        <EditLoanDetailsSuccess
          closeEditLoanDetailsSuccess={closeEditLoanDetailsSuccess}
        />
      )}
      {openEditLoanDetails && (
        <EditLoanDetails
          closeEditLoanDetails={closeEditLoanDetails}
          loading={false}
          loanEditDetails={loanEditDetails}
          setLoanEditDetails={setLoanEditDetails}
          handleUpdateLoanDetails={handleUpdateLoanDetails}
        />
      )}

      {openEditChattelSuccess && (
        <EditChattelSuccess closeEditChattelSuccess={closeEditChattelSuccess} />
      )}
      {openEditChattel && (
        <EditChattel
          closeEditChattel={closeEditChattel}
          loading={loadingEditBorrowerChattel}
          chattelEditDetails={borrowerChattelEditDetails}
          setChattelEditDetails={setBorrowerChattelEditDetails}
          selectedChattel={selectedBorrowerChattel}
          handleEditChattel={handleEditChattel}
          toggleEditChattelSuccess={toggleEditChattelSuccess}
          refetchLoanApplication={refetchAppilcation}
        />
      )}
      {openEditGuarantorChattelSuccess && (
        <EditGuarantorChattelSuccess
          closeEditGuarantorChattelSuccess={closeEditGuarantorChattelSuccess}
        />
      )}
      {openEditGuarantorChattel && (
        <EditGuarantorChattel
          closeEditGuarantorChattel={closeEditGuarantorChattel}
          loading={loadingEditGuarantorChattel}
          guarantorChattelEditDetails={guarantorChattelEditDetails}
          setGuarantorChattelEditDetails={setGuarantorChattelEditDetails}
          chattel={selectedChattel}
          handleEditGuarantorChattel={handleEditGuarantorChattel}
          refetchLoanApplication={refetchAppilcation}
          toggleEditGuarantorChattelSuccess={toggleEditGuarantorChattelSuccess}
        />
      )}
      {openEditGuarantor && (
        <EditGuarantor
          closeEditGuarantor={closeEditGuarantor}
          loading={loadingEditGuarantor}
          guarantorEditDetails={guarantorEditDetails}
          setGuarantorEditDetails={setGuarantorEditDetails}
          handleEditGuarantor={handleEditGuarantor}
        />
      )}

      {openEditGuarantorSuccess && (
        <EditGuarantorSuccess
          closeEditGuarantorSuccess={closeEditGuarantorSuccess}
        />
      )}
      {openReassignOfficer && (
        <ReassignOfficer
          closeReassignOfficer={closeReassignOfficer}
          handleCollectDetails={handleCollectReassignDetails}
          loading={false}
          reassignDetails={reassignDetails}
          setReassignDetails={setReassignDetails}
        />
      )}

      {openReassignPrompt && (
        <ReassignPrompt
          closeReassignPrompt={closeReassignPrompt}
          handleAcceptPrompt={handleReassignOfficer}
          reassignDetails={reassignDetails}
          loading={loadingReassignment}
        />
      )}

      {openReassignSuccess && (
        <ReaasignSuccess
          closeReassignSuccess={closeReassignSuccess}
          reassignDetails={reassignDetails}
        />
      )}

      {openEditResidenceLocation && (
        <EditResidenceLocation
          closeEditResidenceLocation={closeEditResidenceLocation}
          editResidenceLocationDetails={editResidenceLocationDetails}
          setEditResidenceLocationDetails={setEditResidenceLocationDetails}
          loading={loadingEditResidence}
          handleEditResidenceLocation={handleEditResidenceLocation}
        />
      )}

      {openEditResidenceLocationSuccess && (
        <EditResidenceLocationSuccess
          closeEditResidenceLocationSuccess={closeEditResidenceLocationSuccess}
        />
      )}

      {openEditBusinessLocationSuccess && (
        <EditBusinessLocationSuccess
          closeEditBusinessLocationSuccess={closeEditBusinessLocationSuccess}
        />
      )}
      {openEditBusinessLocation && (
        <EditBusinessLocation
          closeEditBusinessLocation={closeEditBusinessLocation}
          editBusinessLocationDetails={editBusinessLocationDetails}
          setEditBusinessLocationDetails={setEditBusinessLocationDetails}
          loading={loadingEditBusiness}
          handleEditBusinessLocation={handleEditBusinessLocation}
        />
      )}
      {openEditIncomeSuccess && (
        <EditIncomeSuccess closeEditIncomeSuccess={closeEditIncomeSuccess} />
      )}
      {openEditKinSuccess && (
        <EditKinSuccess closeEditKinSuccess={closeEditKinSuccess} />
      )}
      {openEditPersonalDetailsSuccess && (
        <EditPersonalDetailsSuccess
          closeEditPersonalDetailsSuccess={closeEditPersonalDetailsSuccess}
        />
      )}
      {openEditPersonalDetails && (
        <EditPersonalDetails
          closeEditPersonalDetails={closeEditPersonalDetails}
          borrowerEditDetails={borrowerEditDetails}
          setBorrowerEditDetails={setBorrowerEditDetails}
          handleEditPersonalDetails={handleEditPersonalDetails}
          isLoadingPersonalDetails={isLoadingPersonalDetails}
        />
      )}

      {openEditKinDetails && (
        <EditKin
          closeEditKinDetails={closeEditKinDetails}
          kinEditDetails={kinEditDetails}
          setKinEditDetails={setKinEditDetails}
          handleEditNextOfKinDetails={handleEditNextOfKinDetails}
          loadingUpdateKin={loadingUpdateKin}
        />
      )}

      {openEditIncomeDetails && (
        <EditIncomeDetails
          closeEditIncomeDetails={closeEditIncomeDetails}
          incomeEditDetails={incomeEditDetails}
          setIncomeEditDetails={setIncomeEditDetails}
          handleEditIncomeDetails={handleEditIncomeDetails}
          loadingEditIncome={loadingEditIncome}
        />
      )}

      {openPromptApproval && (
        <Prompt
          closePromptApproval={closeOpenPromptApproval}
          handleContinueApproval={handleContinueApproval}
        />
      )}

      {openLoanDetails && (
        <LoanDetails
          closeLoanDetails={closeLoanDetails}
          handleContinueChecklist={handleContinueChecklist}
          handleFinalizeDecline={handleCompleteDeclineChecklist}
          loanApplication={loanApplication}
        />
      )}

      {openChecklist && (
        <CheckList
          closeChecklist={closeChecklist}
          handleContinueApproveAmount={handleContinueApproveAmount}
          handleBackToLoanDetailsFromChecklist={
            handleBackToLoanDetailsFromChecklist
          }
          approvalDetails={approvalDetails}
          setApprovalDetails={setApprovalDetails}
        />
      )}

      {openDeclineChecklist && (
        <DeclineChecklist
          closeDeclineChecklist={closeDeclineChecklist}
          handleContinueApproveAmount={handleFinalizeDecline}
          handleBackToLoanDetailsFromChecklist={
            handleBackToLoanDetailsFromDeclineChecklist
          }
          approvalDetails={approvalDetails}
          setApprovalDetails={setApprovalDetails}
        />
      )}

      {openApprovedAmount && (
        <ApprovedAmount
          closeLoanAmount={closeApprovedAmount}
          handleFinalizeApproval={handleFinalizeApproval}
          approvalDetails={approvalDetails}
          setApprovalDetails={setApprovalDetails}
        />
      )}

      {openFinalizeApproval && (
        <Finalize
          closeFinalizeApproval={closeFinalizeApproval}
          handleApproveLoan={handleApproveLoan}
          handleBackToAmountFromFinalizeApproval={
            handleBackToAmountFromFinalizeApproval
          }
          approvalDetails={approvalDetails}
          setApprovalDetails={setApprovalDetails}
          approvalLoading={approvalLoading}
        />
      )}

      {openFinalizeDecline && (
        <FinalizeDecline
          closeFinalizeDecline={closeFinalizeDecline}
          handleDeclineLoan={handleDeclineLoan}
          handleBackLoanDetailsFromFinalizeDecline={
            handleBackLoanDetailsFromFinalizeDecline
          }
          approvalDetails={approvalDetails}
          setApprovalDetails={setApprovalDetails}
          declineLoading={declineLoading}
        />
      )}

      {openApproveSuccess && (
        <ApproveSuccess closeApproveSuccess={closeApproveSuccess} />
      )}

      {openDeclineSuccess && (
        <DeclineSuccess closeDeclineSuccess={closeDeclineSuccess} />
      )}

      {isFetching ? (
        <SingleLoanSkeleton />
      ) : (
        <div className="w-full mb-[30px] pl-[10px]">
          <div className="flex items-center h-[50px] mb-[10px] justify-between">
            <div className="flex items-center gap-[5px]">
              <ChevronLeft color="#000000" size={17} />
              <span
                onClick={() => navigate("/dashboard/loans-applications")}
                className="cursor-pointer"
              >
                Loan applications
              </span>
              <ChevronRight color="#000000" size={17} />
              <span className="underline font-bold text-[16px] text-[#222222]">
                {loanApplication?.loanCode}
              </span>
            </div>
            <div className="flex items-center gap-[10px]">
              <User color="#000000" size={16} />
              <span className="text-[#000000] text-[14px] font-normal">
                {loanApplication?.customer?.firstname}{" "}
                {loanApplication?.customer?.lastname}
              </span>
              <button
                className={`bg-[#F3F3F3] h-[40px] capitalize rounded-[30px] text-[14px] font-normal px-[20px] flex items-center justify-center ${
                  loanApplication?.status?.toLocaleLowerCase() === "approved"
                    ? "text-[#00A676]"
                    : loanApplication?.status?.toLocaleLowerCase() ===
                        "declined"
                      ? "text-[#000000]"
                      : "text-[#FFA500]"
                }`}
              >
                <Dot size={40} />
                {loanApplication?.status}
              </button>
            </div>
          </div>
          <div className="h-[180px] w-full bg-[#000000] mb-[10px] p-[15px] flex flex-col justify-between rounded-[10px] border border-[#FFDEDE]">
            <div className="flex items-center justify-between">
              <p className="text-[#F3F3F3] text-[16px] font-medium">
                Potential Repayment Amount (Ksh)
              </p>
              <div className="flex flex-col items-end">
                <p className="font-medium leading-none text-[12px] text-white">
                  Loan application completed on
                </p>
                <p className="font-medium text-[14px] text-white">
                  {formattedDate(loanApplication?.updatedAt) ?? "N/A"}
                </p>
              </div>
            </div>
            <p className="text-[40px] font-semibold text-[#F3F3F3]">
              {formatAmount(loanApplication?.repaymentAmount)}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <p className="font-medium text-[13px] text-[#F3F3F3]">
                  Loan Amount
                </p>
                <p className="font-medium text-[14px] text-white">
                  {formatAmount(loanApplication?.loanAmount ?? 0) ?? "N/A"}
                </p>
              </div>
              <div className="flex flex-col items-end">
                <p className="font-medium leading-none text-[12px] text-white">
                  Approval Date
                </p>
                <p className="font-medium text-[14px] text-white">
                  {formattedDate(loanApplication?.approvedAt) ?? "N/A"}
                </p>
              </div>
            </div>
          </div>
          <div className="my-[15px] rounded-[10px] w-full h-[160px] flex flex-col justify-around bg-[#DEFFF4] p-[10px]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#000000] text-[16px] font-normal">
                  Approval Status ({generalStatus})
                </p>
                <p className="text-[14px] text-[#000000] italic font-light">
                  This loan application is complete and is ready for review
                </p>
              </div>
              {generalStatus === "Not approved" && (
                <button
                  onClick={toggleOpenPromptApproval}
                  className="bg-[#000000] text-white h-[35px] px-[20px] rounded-[30px] text-[15px]"
                >
                  Approve Loan
                </button>
              )}
              {generalStatus === "Declined" && (
                <button className="bg-[#000000] text-white h-[35px] px-[20px] rounded-[30px] text-[15px]">
                  Application Declined
                </button>
              )}
              {generalStatus === "In progress" && (
                <button
                  onClick={toggleOpenPromptApproval}
                  className="bg-[#000000] text-white h-[35px] px-[20px] rounded-[30px] text-[15px]"
                >
                  Continue Approval
                </button>
              )}
            </div>
            {approvals?.length > 0 &&
              approvals?.map((item, index) => (
                <div key={index} className="flex my-[8px] items-center">
                  <p className="w-[25%] capitalize font-light text-[12px]">
                    {item?.stage} Approval
                  </p>
                  <p className="w-[25%] font-normal text-[12px]">
                    {item?.totalApproved || item?.totalDeclined}/
                    {item?.totalRequired} Approvals
                  </p>
                  <p
                    className={`w-[50%] text-[12px] font-semibold ${
                      generalStatus === "Declined"
                        ? "text-[#000000]"
                        : generalStatus === "Approved"
                          ? "text-[#00A36C]"
                          : "text-[#FFA500]"
                    }`}
                  >
                    {generalStatus === "Declined"
                      ? "Declined"
                      : item?.approvals?.length > 0 &&
                          item?.approvals?.length !== item?.totalRequired
                        ? "In Progress"
                        : item?.approvals?.length > 0 &&
                            item?.approvals?.length === item?.totalRequired
                          ? "Complete"
                          : item?.approvals?.length === 0
                            ? "Not Started"
                            : "In Progress"}
                  </p>
                </div>
              ))}
          </div>
          <div className="h-[40px] flex justify-between my-[20px] w-full">
            {[
              "Borrower",
              "Loan Application Details",
              "Guarantors",
              "Loan Officer",
              "Action",
            ].map((tab) => (
              <div
                key={tab}
                onClick={() => switchTab(tab)}
                className={tabClass(tab)}
              >
                {tab}
              </div>
            ))}
          </div>
          {activeTab === "Borrower" && (
            <>
              <div>
                {loanApplication?.customer?.selfie_image?.length > 0 ? (
                  <div className="w-[90px] h-[90px] my-[10px] rounded-[20px] relative flex items-center justify-center">
                    <img
                      className="h-full w-full rounded-[20px] object-cover"
                      src={loanApplication?.customer?.selfie_image}
                      alt="user"
                    />
                    <div
                      onClick={toggleAddSelfie}
                      className="absolute h-[40px] cursor-pointer shadow-lg bg-white w-[40px] right-[-20px] top-[-20px] rounded-full flex items-center justify-center"
                    >
                      <Plus size={17} color="blue" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full flex items-center justify-between my-[20px]">
                    <p className="text-[#222222] cursor-pointer text-[14px] font-medium">
                      Selfie unavailable
                    </p>
                    <button
                      onClick={toggleAddSelfie}
                      className="bg-[#ffffff] text-[#000000] h-[35px] px-[20px] rounded-[30px] border border-[#000000] text-[15px] flex items-center justify-center gap-[5px]"
                    >
                      <Plus size={16} />
                      Add Selfie
                    </button>
                  </div>
                )}
                <div className="h-[35px] mt-[10px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                  <p className="text-[#000000] font-medium text-[20px]">
                    Personal Details
                  </p>
                  <p
                    onClick={toggleEditPersonalDetails}
                    className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                  >
                    Edit
                  </p>
                </div>
                <div className="flex items-center justify-between my-[10px]">
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      ID Number
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.identification ?? "N/A"
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      First Name
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.firstname ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Middle Name
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.middlename ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Last Name
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.lastname ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Date of Birth
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.dob ?? "N/A"}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between my-[20px]">
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Gender
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.gender ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Phone Number
                    </p>
                    <CopyDataCard
                      width="full"
                      myData={loanApplication?.customer?.mobileno ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Alt Phone Number
                    </p>
                    <CopyDataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.altPhoneNumber ?? "N/A"
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[39%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Email
                    </p>
                    <CopyDataCard
                      width="full"
                      myData={loanApplication?.customer?.email ?? "N/A"}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-[1.25%] my-[20px]">
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Marital Status
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.maritalStatus ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Number of Dependants
                    </p>
                    <DataCard
                      width="full"
                      myData={loanApplication?.customer?.dependants ?? "N/A"}
                    />
                  </div>
                  <div className="flex flex-col w-[39%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      P.O Box
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.postal_address ?? "N/A"
                      }
                    />
                  </div>
                </div>

                {/* Personal docs */}
                <div className="my-[20px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Borrower’s Identification Documents
                    </p>
                    <p
                      onClick={toggleAddNationalId}
                      className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                    >
                      {loanApplication?.customer?.id_front_image?.length > 0
                        ? "Replace ID"
                        : "Add National Id"}
                    </p>
                  </div>
                  <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                    <div className="w-[20%] flex items-center gap-[5px]">
                      <p className="text-[14px] text-[#222222] font-normal">
                        ID
                      </p>
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      UPDATED
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      NAME
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      STATUS
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      DOWNLOAD
                    </div>
                  </div>
                  {loanApplication?.customer?.id_front_image?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Id front image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.id_front_image,
                              "id_front_image",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <Eye
                          onClick={() =>
                            handleViewDocument(
                              loanApplication?.customer?.id_front_image,
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                      </div>
                    </div>
                  )}

                  <AnimatePresence>
                    {viewUrl && (
                      <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                        {/* Backdrop */}
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          onClick={() => setViewUrl(null)}
                          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        />

                        {/* Modal Content */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="relative max-w-4xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                        >
                          {/* Header */}
                          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
                              Document Preview
                            </h3>
                            <button
                              onClick={() => setViewUrl(null)}
                              className="text-slate-400 hover:text-slate-900 transition-colors"
                            >
                              <X size={20} />
                            </button>
                          </div>

                          {/* Image Container */}
                          <div className="bg-slate-50 p-4 flex justify-center items-center overflow-auto max-h-[70vh]">
                            <img
                              src={viewUrl}
                              alt="Preview"
                              className="rounded-lg shadow-sm max-w-full h-auto object-contain"
                            />
                          </div>

                          {/* Footer */}
                          <div className="p-4 bg-white flex justify-end">
                            <button
                              onClick={() => setViewUrl(null)}
                              className="px-8 py-3 bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl hover:bg-black transition-all"
                            >
                              Close
                            </button>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </AnimatePresence>
                  {loanApplication?.customer?.id_back_image?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Id Back image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.id_back_image,
                              "id_back_image",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <Eye
                          onClick={() =>
                            handleViewDocument(
                              loanApplication?.customer?.id_back_image,
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.passport_image?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Passport id image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.passport_image,
                              "Passport",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <Eye
                          onClick={() =>
                            handleViewDocument(
                              loanApplication?.customer?.passport_image,
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.signatureUrl?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Signature image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.signatureUrl,
                              "signature",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <Eye
                          onClick={() =>
                            handleViewDocument(
                              loanApplication?.customer?.signatureUrl,
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* next of kin */}
                <div className="h-[35px] mt-[10px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                  <p className="text-[#000000] font-medium text-[20px]">
                    Next of Kin Details
                  </p>
                  <p
                    onClick={toggleEditKinDetails}
                    className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                  >
                    Edit
                  </p>
                </div>
                <div className="flex items-center justify-between my-[10px]">
                  <div className="flex flex-col w-[39%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Full Name
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.nextOfKins?.[0]?.name ??
                        "N/A"
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Relationship
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.nextOfKins?.[0]
                          ?.relationship ?? "N/A"
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Phone Number
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.nextOfKins?.[0]
                          ?.phoneNumber ?? "N/A"
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Residence
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.nextOfKins?.[0]?.location ??
                        "N/A"
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center gap-[15px] my-[10px]">
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Nearest Landmark
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.nextOfKins?.[0]?.location ??
                        "N/A"
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[19%]">
                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                      Date of Birth
                    </p>
                    <DataCard
                      width="full"
                      myData={
                        loanApplication?.customer?.nextOfKins?.[0]
                          ?.dateOfBirth ?? "N/A"
                      }
                    />
                  </div>
                </div>

                {/* income */}
                <div className="my-[30px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Income Details
                    </p>
                    <p
                      onClick={toggleEditIncomeDetails}
                      className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                    >
                      Edit
                    </p>
                  </div>

                  {/* Row 1 */}
                  <div className="flex items-center justify-between my-[10px]">
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Source of Income
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.sourceOfIncome ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        KRA PIN
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.businessKraPin ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Business Name
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.businessName ?? "N/A"
                        }
                      />
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="flex items-center justify-between my-[10px]">
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Years of Operation
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.yearsOfOperation ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Type of Business
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.typeOfBusiness ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Business License
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.businessLicense ?? "N/A"
                        }
                      />
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="flex items-center justify-between my-[10px]">
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Employer Name
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.employerName ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Monthly Income
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.monthlyIncome ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Duration at Employer
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.durationAtEmployer ?? "N/A"
                        }
                      />
                    </div>
                  </div>

                  {/* Row 4 */}
                  <div className="flex items-center justify-between my-[10px]">
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Level of Stability
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.levelOfStability ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Strength of Business
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.strengthOfBusiness ?? "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Risks Associated
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.incomes?.[0]
                            ?.risksAssociated ?? "N/A"
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* income docs */}
                <div className="my-[20px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Borrower’s Income Documents
                    </p>
                  </div>
                  <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                    <div className="w-[20%] flex items-center gap-[5px]">
                      <p className="text-[14px] text-[#222222] font-normal">
                        ID
                      </p>
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      UPDATED
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      NAME
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      STATUS
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      DOWNLOAD
                    </div>
                  </div>
                  {loanApplication?.customer?.incomes[0]?.payslips?.length >
                    0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.customer?.incomes[0]?.id?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Payslips(
                        {
                          loanApplication?.customer?.incomes[0]?.payslips
                            ?.length
                        }
                        )
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.incomes[0]?.payslips,
                              "payslips",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <p
                          onClick={toggleAddPayslips}
                          className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                        >
                          View
                        </p>
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.incomes[0]?.mpesaStatementUrl
                    ?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.customer?.incomes[0]?.id?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Mpesa Statement
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.incomes[0]
                                ?.mpesaStatementUrl,
                              "signature",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <p
                          onClick={toggleAddMpesaStatement}
                          className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                        >
                          Replace
                        </p>
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.incomes[0]?.bankStatementUrl
                    ?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.customer?.incomes[0]?.id?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Bank Statement
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                        <Download
                          onClick={() =>
                            downloadFromUrl(
                              loanApplication?.customer?.incomes[0]
                                ?.bankStatementUrl,
                              "signature",
                            )
                          }
                          color="#000000"
                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                        />
                        <p
                          onClick={toggleAddBankStatement}
                          className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                        >
                          Replace
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Business location */}
                <div className="my-[30px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Business Location
                    </p>
                    <p
                      onClick={toggleEditBusinessLocation}
                      className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                    >
                      Edit
                    </p>
                  </div>
                  <div className="flex items-center justify-between my-[10px]">
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Latitude & Longitude
                      </p>
                      <CopyDataLocation
                        width="full"
                        myData={`${decimalToDMS(
                          loanApplication?.customer?.businesses?.[0]
                            ?.businessLatitude,
                          true,
                        )} ${decimalToDMS(
                          loanApplication?.customer?.businesses?.[0]
                            ?.businessLongitude,
                          false,
                        )}`}
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Physical Address
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.businesses?.[0]?.businessPhysicalAddress?.trim() ||
                          "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Location Description
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.businesses?.[0]?.businessLocationDescription?.trim() ||
                          "N/A"
                        }
                      />
                    </div>
                  </div>
                  {loanApplication?.customer?.businesses?.[0]
                    ?.businessImageUrl && (
                    <div className="w-[32%] h-[150px] my-[20px] rounded-[10px] bg-white">
                      <img
                        className="h-full w-full object-cover rounded-[10px]"
                        src={
                          loanApplication.customer.businesses[0]
                            .businessImageUrl
                        }
                        alt="business location"
                      />
                    </div>
                  )}
                </div>

                {/* Residence location */}
                <div className="my-[30px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Residential Location
                    </p>
                    <p
                      onClick={toggleEditResidenceLocation}
                      className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                    >
                      Edit
                    </p>
                  </div>
                  <div className="flex items-center justify-between my-[10px]">
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Latitude & Longitude
                      </p>
                      <CopyDataLocation
                        width="full"
                        myData={`${decimalToDMS(
                          loanApplication?.customer?.residences?.[0]
                            ?.residenceLatitude,
                          true,
                        )} ${decimalToDMS(
                          loanApplication?.customer?.residences?.[0]
                            ?.residenceLongitude,
                          false,
                        )}`}
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Physical Address
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.residences?.[0]?.residencePhysicalAddress?.trim() ||
                          "N/A"
                        }
                      />
                    </div>
                    <div className="flex flex-col w-[32%]">
                      <p className="text-[#5C5C5C] text-[14px] font-medium">
                        Location Description
                      </p>
                      <DataCard
                        width="full"
                        myData={
                          loanApplication?.customer?.residences?.[0]?.residenceLocationDescription?.trim() ||
                          "N/A"
                        }
                      />
                    </div>
                  </div>
                  {loanApplication?.customer?.residences?.[0]
                    ?.residenceImageUrl && (
                    <div className="w-[32%] h-[150px] my-[20px] rounded-[10px] bg-white">
                      <img
                        className="h-full w-full object-cover rounded-[10px]"
                        src={
                          loanApplication.customer.residences[0]
                            .residenceImageUrl
                        }
                        alt="business location"
                      />
                    </div>
                  )}
                </div>

                {/* Borrower docs */}
                <div className="my-[20px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Borrower’s Application Documents
                    </p>
                    <p
                      onClick={toggleAllDocuments}
                      className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                    >
                      Add
                    </p>
                  </div>
                  {loanApplication?.documents?.length > 0 ? (
                    loanApplication?.documents?.map((doc) => (
                      <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                        <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                          {doc?.id?.slice(0, 7)}
                        </div>
                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                          {formattedDate(doc?.uploadedAt)}
                        </div>
                        <div className="w-[20%] capitalize text-[14px] text-[#00A676] font-normal">
                          {doc?.documentName}
                        </div>
                        <div className="w-[20%] capitalize text-[14px] text-[#00A676] font-normal">
                          Active
                        </div>
                        <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                          <Download
                            onClick={() =>
                              downloadFromUrl(doc?.fileUrl, doc?.documentName)
                            }
                            color="#000000"
                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                          />
                          <Trash
                            onClick={() => handleDeleteDocument(doc)}
                            color="#000000"
                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 mt-4">
                      <InfoIcon className="w-5 h-5 text-gray-400" />
                      <span>No more borrower document available</span>
                    </div>
                  )}
                </div>

                {/* chattels */}
                <div className="my-[20px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Chattels
                    </p>
                    <p
                      onClick={toggleLoanChattels}
                      className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                    >
                      Add chattel
                    </p>
                  </div>
                  <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                    <div className="w-[20%] flex items-center gap-[5px]">
                      <p className="text-[14px] text-[#222222] font-normal">
                        ID
                      </p>
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      UPDATED
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      NAME
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      CHATTEL LOCATION
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      DOWNLOAD
                    </div>
                  </div>
                  {loanApplication?.customer?.chattels?.length > 0 ? (
                    loanApplication?.customer?.chattels?.map((item) => (
                      <div
                        key={item.id}
                        className="h-[35px] my-[10px] pl-[10px] flex items-center"
                      >
                        <div className="w-[20%] flex items-center gap-[5px]">
                          <p
                            onClick={() => toggleChattel(item)}
                            className="text-[14px] text-[#FF5757] cursor-pointer"
                          >
                            {item?.id?.substring(0, 6)}
                          </p>
                        </div>
                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                          {formattedDate(item.updatedAt)}
                        </div>
                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                          {item.name}
                        </div>
                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                          {item.location}
                        </div>
                        <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                          <Download
                            onClick={() =>
                              downloadFromUrl(item?.images, item?.name)
                            }
                            color="#000000"
                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                          />
                          <Eye
                            onClick={() => toggleChattel(item)}
                            color="#000000"
                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                          />
                          <SquarePen
                            onClick={() => toggleEditChattel(item)}
                            color="#000000"
                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 mt-4">
                      <InfoIcon className="w-5 h-5 text-gray-400" />
                      <span>No chattel data available</span>
                    </div>
                  )}
                </div>

                {/* docs status */}
                <div className="my-[20px] w-full">
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Personal Document(s) Status
                    </p>
                  </div>
                  <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                    <div className="w-[20%] flex items-center gap-[5px]">
                      <p className="text-[14px] font-normal">ID</p>
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      UPDATED AT
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      NAME
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      UPLOAD STATUS
                    </div>
                    <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                      DOC STATUS
                    </div>
                  </div>
                  {loanApplication?.customer?.id_front_image?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Id Front Image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal flex items-center gap-2">
                        <CheckCircle className="text-green-600 w-5 h-5" />
                        <span className="text-green-700">Signed</span>
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.id_back_image?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Id Back Image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal flex items-center gap-2">
                        <CheckCircle className="text-green-600 w-5 h-5" />
                        <span className="text-green-700">Signed</span>
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.passport_image?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Passport Id Image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal flex items-center gap-2">
                        <CheckCircle className="text-green-600 w-5 h-5" />
                        <span className="text-green-700">Signed</span>
                      </div>
                    </div>
                  )}
                  {loanApplication?.customer?.signatureUrl?.length > 0 && (
                    <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                      <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                        {loanApplication?.loanCode?.slice(0, 7)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                        {formattedDate(loanApplication?.customer?.updatedAt)}
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Signature Image
                      </div>
                      <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                        Active
                      </div>
                      <div className="w-[20%] text-[14px] text-[#222222] font-normal flex items-center gap-2">
                        <CheckCircle className="text-green-600 w-5 h-5" />
                        <span className="text-green-700">Signed</span>
                      </div>
                    </div>
                  )}
                  <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                    <p className="text-[#000000] font-medium text-[20px]">
                      Loan Document(s) Status
                    </p>
                  </div>
                  {loanApplication?.documents?.length > 0 ? (
                    loanApplication?.documents?.map((doc) => (
                      <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                        <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                          <input type="checkbox" name={`doc-1`} id={`doc-1`} />
                          {doc?.id?.slice(0, 7)}
                        </div>
                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                          {formattedDate(doc?.uploadedAt)}
                        </div>
                        <div className="w-[20%] capitalize text-[14px] text-[#00A676] font-normal">
                          {doc?.documentName}
                        </div>
                        <div className="w-[20%] capitalize text-[14px] text-[#00A676] font-normal">
                          Active
                        </div>
                        <div className="w-[20%] text-[14px] text-[#222222] font-normal flex items-center gap-2">
                          <CheckCircle className="text-green-600 w-5 h-5" />
                          <span className="text-green-700">Signed</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 text-gray-500 mt-4">
                      <InfoIcon className="w-5 h-5 text-gray-400" />
                      <span>No more borrower document available</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {activeTab === "Loan Application Details" && (
            <div className="w-full my-[20px]">
              <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                <p className="text-[#000000] font-medium text-[20px]">
                  Loan Details
                </p>
                <p
                  onClick={toggleEditLoanDetails}
                  className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                >
                  Edit
                </p>
              </div>
              <div className="flex my-[20px] flex-wrap gap-[20px]">
                {/* Loan Code */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Loan Code
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanCode)}
                  />
                </div>

                {/* Loan Amount */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Loan Amount
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      formatAmount(loanApplication?.loanAmount),
                    )}
                  />
                </div>

                {/* Interest Rate */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Interest Rate
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.interestRate)}
                  />
                </div>

                {/* Loan Period */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Loan Period
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanPeriod)}
                  />
                </div>

                {/* Repayment Amount */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Repayment Amount
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      formatAmount(loanApplication?.repaymentAmount),
                    )}
                  />
                </div>

                {/* Status */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Status
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.status)}
                  />
                </div>

                {/* Created At */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Created At
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      formattedDate(loanApplication?.createdAt),
                    )}
                  />
                </div>

                {/* Updated At */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Updated At
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      formattedDate(loanApplication?.updatedAt),
                    )}
                  />
                </div>

                {/* Submitted At */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Submitted At
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.submittedAt)}
                  />
                </div>

                {/* Approved At */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Approved At
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      formattedDate(loanApplication?.approvedAt),
                    )}
                  />
                </div>

                {/* Repayment Frequency */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Repayment Frequency
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.repaymentFrequency)}
                  />
                </div>

                {/* Grace Period */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Grace Period
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.gracePeriod)}
                  />
                </div>

                {/* Source */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Source
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.source)}
                  />
                </div>

                {/* Loan Purpose */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Loan Purpose
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanPurpose)}
                  />
                </div>

                {/* Application Stage */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Application Stage
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.application_stage)}
                  />
                </div>
              </div>
            </div>
          )}
          {activeTab === "Loan Officer" && (
            <div>
              <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                <p className="text-[#000000] font-medium text-[20px]">
                  Loan Officer
                </p>
              </div>
              <div className="flex my-[20px] flex-wrap gap-[20px]">
                {/* Public ID */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Public ID
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      loanApplication?.loanOfficer?.public_id,
                    )}
                  />
                </div>
                {/* First Name */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    First Name
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      loanApplication?.loanOfficer?.firstname,
                    )}
                  />
                </div>
                {/* Last Name */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Last Name
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanOfficer?.lastname)}
                  />
                </div>
                {/* Phone */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Phone
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanOfficer?.phone)}
                  />
                </div>
                {/* Email */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Email
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanOfficer?.email)}
                  />
                </div>
                {/* Status */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Status
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanOfficer?.status)}
                  />
                </div>
                {/* Country */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Country
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(loanApplication?.loanOfficer?.country)}
                  />
                </div>
                {/* Department */}
                <div className="flex flex-col w-full sm:w-[48%] lg:w-[32%]">
                  <p className="text-[#5C5C5C] text-[14px] font-medium">
                    Department
                  </p>
                  <DataCard
                    width="full"
                    myData={formatValue(
                      loanApplication?.loanOfficer?.department,
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {activeTab === "Guarantors" && (
            <>
              <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                <p className="text-[#000000] font-medium text-[20px]">
                  Guarantors
                </p>
                <p
                  onClick={toggleAddLoanGuarantor}
                  className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                >
                  Add guarantor
                </p>
              </div>
              <div className="my-[10px] w-full">
                {loanApplication?.guarantors &&
                loanApplication?.guarantors?.length > 0 ? (
                  loanApplication?.guarantors?.map((guarantor, index) => {
                    const isOpen = openIndexes?.includes(index);
                    return (
                      <div
                        key={index}
                        className="w-full mb-[10px] rounded-[8px]"
                      >
                        <div
                          className="h-[60px] px-[10px] bg-white w-full flex items-center justify-between cursor-pointer"
                          onClick={() => toggleGuarantor(index)}
                        >
                          <div>
                            <p className="text-gray-400 text-[14px] font-medium">
                              Guarantor {index + 1}
                            </p>
                            <p className="text-[#000000] text-[16px] font-medium">
                              {guarantor?.guarantorName ?? "N/A"}
                            </p>
                          </div>
                          <div className="flex items-center gap-[10px]">
                            <ChevronRight
                              color="#000000"
                              size={23}
                              className={`transition-transform duration-300 ${
                                isOpen ? "rotate-90" : ""
                              }`}
                            />
                          </div>
                        </div>

                        {/* Expandable content */}
                        {isOpen && (
                          <div className="w-full h-full bg-[#FEF9F9]">
                            <div className="px-[10px] w-full bg-white">
                              <div className="mb-[30px] w-full">
                                <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                                  <p className="text-[#000000] font-medium text-[20px]">
                                    Guarantor Details
                                  </p>
                                  <p
                                    onClick={() =>
                                      toggleEditGuarantor(guarantor)
                                    }
                                    className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                                  >
                                    Edit
                                  </p>
                                </div>
                                {guarantor?.guarantorPhoto?.trim()?.length >
                                  0 && (
                                  <div className="w-full h-[180px] my-[20px]">
                                    <p className="text-[#000000] font-medium text-[20px]">
                                      Guarantor Photo
                                    </p>
                                    <div className="h-[150px] w-[200px] rounded-[20px]">
                                      <img
                                        className="h-full w-full rounded-[20px] object-cover"
                                        src={guarantor?.guarantorPhoto}
                                        alt="guarantor-photo"
                                      />
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-center justify-between my-[10px]">
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Name
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={guarantor?.guarantorName ?? "N/A"}
                                    />
                                  </div>
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Residence
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        guarantor?.residenceLocation ?? "N/A"
                                      }
                                    />
                                  </div>
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Phone number
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        guarantor?.guarantorPhone ?? "N/A"
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-[2%] my-[10px]">
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Email
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        guarantor?.guarantorEmail ?? "N/A"
                                      }
                                    />
                                  </div>
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Guarantee Amount
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        formatAmount(
                                          guarantor?.guaranteeAmount,
                                        ) ?? 0
                                      }
                                    />
                                  </div>
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Guarantee Reason
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={guarantor?.reason ?? 0}
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-[2%] my-[10px]">
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Date
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        formattedDate(guarantor?.createdAt) ??
                                        "N/A"
                                      }
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* location */}
                              <div className="my-[30px] w-full">
                                <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                                  <p className="text-[#000000] font-medium text-[20px]">
                                    Guarantor Location
                                  </p>
                                  <p
                                    onClick={() =>
                                      toggleEditGuarantorLocation(guarantor)
                                    }
                                    className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                                  >
                                    Edit
                                  </p>
                                </div>
                                <div className="flex items-center justify-between my-[10px]">
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Latitude & Longitude
                                    </p>
                                    <CopyDataLocation
                                      width="full"
                                      myData={`${decimalToDMS(
                                        guarantor?.residenceLatitude,
                                        true,
                                      )} ${decimalToDMS(
                                        guarantor?.residenceLongitude,
                                        false,
                                      )}`}
                                    />
                                  </div>
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Physical Address
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        guarantor?.residenceLocation?.trim() ||
                                        "N/A"
                                      }
                                    />
                                  </div>
                                  <div className="flex flex-col w-[32%]">
                                    <p className="text-[#5C5C5C] text-[14px] font-medium">
                                      Location Description
                                    </p>
                                    <DataCard
                                      width="full"
                                      myData={
                                        guarantor?.residenceLocation?.trim() ||
                                        "N/A"
                                      }
                                    />
                                  </div>
                                </div>
                                {guarantor?.guarantorBusinessPhoto && (
                                  <div className="w-[32%] h-[150px] my-[20px] rounded-[10px] bg-white">
                                    <img
                                      className="h-full w-full object-cover rounded-[10px]"
                                      src={guarantor?.guarantorBusinessPhoto}
                                      alt="business location"
                                    />
                                  </div>
                                )}
                              </div>
                              {/* chattels */}
                              <div className="my-[20px] w-full">
                                <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                                  <p className="text-[#000000] font-medium text-[20px]">
                                    Guarantor Chattels
                                  </p>
                                  <p
                                    onClick={() =>
                                      toggleAddGuarantorChattel(guarantor)
                                    }
                                    className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                                  >
                                    Add chattel
                                  </p>
                                </div>
                                <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                                  <div className="w-[20%] flex items-center gap-[5px]">
                                    <p className="text-[14px] text-[#222222] font-normal">
                                      ID
                                    </p>
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    UPDATED
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    NAME
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    CHATTEL LOCATION
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    DOWNLOAD
                                  </div>
                                </div>
                                {guarantor?.chattels?.length > 0 ? (
                                  guarantor?.chattels?.map((item) => (
                                    <div
                                      key={item.id}
                                      className="h-[35px] my-[10px] pl-[10px] flex items-center"
                                    >
                                      <div className="w-[20%] flex items-center gap-[5px]">
                                        <p
                                          onClick={() => toggleChattel(item)}
                                          className="text-[14px] text-[#FF5757] cursor-pointer font-normal"
                                        >
                                          {item?.id?.substring(0, 6)}
                                        </p>
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        {formattedDate(
                                          formattedDate(item.updatedAt),
                                        )}
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        {item.name}
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        {item.location}
                                      </div>
                                      <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                                        <Download
                                          onClick={() =>
                                            downloadFromUrl(
                                              item?.images,
                                              item.name,
                                            )
                                          }
                                          color="#000000"
                                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                                        />
                                        <SquarePen
                                          onClick={() =>
                                            toggleEditGuarantorChattel(item)
                                          }
                                          color="#000000"
                                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                                        />
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="my-[20px] w-full flex items-center gap-[10px]">
                                    <Info size={16} />
                                    <p>No guarantor chattel available</p>
                                  </div>
                                )}
                              </div>
                              {/* docs */}
                              <div className="my-[20px] w-full">
                                <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                                  <p className="text-[#000000] font-medium text-[20px]">
                                    Guarantor’s Personal Documents
                                  </p>
                                </div>
                                <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                                  <div className="w-[20%] flex items-center gap-[5px]">
                                    <p className="text-[14px] text-[#222222] font-normal">
                                      ID
                                    </p>
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    UPDATED
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    NAME
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    STATUS
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    DOWNLOAD
                                  </div>
                                </div>
                                {guarantor?.signatureUrl?.trim()?.length ===
                                  0 ||
                                (guarantor?.signatureUrl === null &&
                                  guarantor?.guarantorBusinessPhoto?.trim()
                                    ?.length === 0) ||
                                guarantor?.guarantorBusinessPhoto === null ? (
                                  <div className="my-[20px] w-full flex items-center gap-[10px]">
                                    <Info size={16} />
                                    <p>No guarantor documents available</p>
                                  </div>
                                ) : (
                                  <>
                                    {guarantor?.signatureUrl?.length > 0 && (
                                      <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                                        <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                                          {loanApplication?.loanCode?.slice(
                                            0,
                                            7,
                                          )}
                                        </div>
                                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                          {formattedDate(guarantor.createdAt)}
                                        </div>
                                        <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                                          Guarantor Signature
                                        </div>
                                        <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                                          Active
                                        </div>
                                        <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                                          <Download
                                            onClick={() =>
                                              downloadFromUrl(
                                                guarantor?.signatureUrl,
                                                "guarantor-signature",
                                              )
                                            }
                                            color="#000000"
                                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {guarantor?.guarantorBusinessPhoto?.length >
                                      0 && (
                                      <div className="h-[35px] my-[10px] pl-[10px] flex items-center">
                                        <div className="w-[20%] text-[14px] flex items-center gap-[5px]">
                                          {loanApplication?.loanCode?.slice(
                                            0,
                                            7,
                                          )}
                                        </div>
                                        <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                          {formattedDate(guarantor.createdAt)}
                                        </div>
                                        <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                                          Guarantor Business Photo
                                        </div>
                                        <div className="w-[20%] text-[14px] text-[#00A676] font-normal">
                                          Active
                                        </div>
                                        <div className="w-[20%] flex gap-4 items-center text-[#222222]">
                                          <Download
                                            onClick={() =>
                                              downloadFromUrl(
                                                guarantor?.guarantorBusinessPhoto,
                                                "guarantorBusinessPhoto",
                                              )
                                            }
                                            color="#000000"
                                            className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                              {/* docs */}
                              <div className="my-[20px] w-full">
                                <div className="h-[35px] w-full border-b-[0.7px] border-[#A9A9A9] flex items-center justify-between">
                                  <p className="text-[#000000] font-medium text-[20px]">
                                    Guarantor’s Documents
                                  </p>
                                  <p
                                    onClick={() =>
                                      toggleGuarantorDocuments(guarantor)
                                    }
                                    className="text-[#037BD1] cursor-pointer text-[14px] font-medium"
                                  >
                                    Add documents
                                  </p>
                                </div>
                                <div className="h-[40px] bg-[#F3F3F3] my-[10px] pl-[10px] flex items-center">
                                  <div className="w-[20%] flex items-center gap-[5px]">
                                    <p className="text-[14px] text-[#222222] font-normal">
                                      ID
                                    </p>
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    UPDATED
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    NAME
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    STATUS
                                  </div>
                                  <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                    DOWNLOAD
                                  </div>
                                </div>
                                {guarantor?.documents?.length > 0 ? (
                                  guarantor?.documents?.map((doc) => (
                                    <div className="h-[35px] bg-white my-[10px] pl-[10px] flex items-center">
                                      <div className="w-[20%] flex items-center gap-[5px]">
                                        <p className="text-[14px] text-[#222222] font-normal">
                                          {doc?.id?.slice(0, 7)}
                                        </p>
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        {formattedDate(doc?.uploadedAt)}
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        {doc?.documentType}
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        ACTIVE
                                      </div>
                                      <div className="w-[20%] text-[14px] text-[#222222] font-normal">
                                        <Download
                                          onClick={() =>
                                            downloadFromUrl(
                                              guarantor?.signatureUrl,
                                              "guarantor-signature",
                                            )
                                          }
                                          color="#000000"
                                          className="w-5 h-5 cursor-pointer hover:scale-110 transition"
                                        />
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="my-[20px] w-full flex items-center gap-[10px]">
                                    <Info size={16} />
                                    <p>No guarantor documents available</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="w-full flex flex-col items-center justify-center text-center py-10 text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 mb-2 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 7a4 4 0 100-8 4 4 0 000 8z"
                      />
                    </svg>
                    <p className="text-[16px] font-medium">
                      No guarantor data available
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
          {activeTab === "Action" && (
            <>
              <div className="h-[400px] p-[10px] mt-[20px] bg-white w-full">
                <p className="font-medium text-[15px] mb-[10px] text-[#000000]">
                  Select what action you wish to perform on this laon
                </p>
                <div className="flex items-center justify-between">
                  <div className="w-[49%]">
                    <ActionCard
                      icon={<Signature color="#000000" />}
                      title="Approve or Decline Loan Application"
                      subtitle="Evaluate and decide on loan applications."
                      handleClick={toggleOpenPromptApproval}
                    />
                  </div>
                  {hasPermission(
                    auth?.roles,
                    "83c47a30-b068-889d-c4e5-e38d756f5bd2",
                  ) && (
                    <div className="w-[49%]">
                      <ActionCard
                        icon={<UserCheck color="#000000" />}
                        handleClick={toggleReassignOfficer}
                        title="Reassign a loan officer"
                        subtitle="Alow a different loan officer to assist a borrower to apply for a loan."
                      />
                    </div>
                  )}
                </div>
                {/* <div className="flex items-center my-[20px] justify-between">
                  <div className="w-[49%]">
                    <ActionCard
                      icon={<History color="#000000" />}
                      title="Update Loan Application Terms"
                      subtitle="Adjust interest rates, repayment schedules, or tenure based on customer requests."
                    />
                  </div>
                  <div className="w-[49%]">
                    <ActionCard
                      icon={<FileText color="#000000" />}
                      title="Add a Journal Payment"
                      subtitle="Record a payment made outside the system, such as via cheque or bank transfer"
                    />
                  </div>
                </div> */}
                {/* <div className="flex items-center my-[20px] justify-between">
                  <div className="w-[49%]">
                    <ActionCard
                      icon={<Bell color="#000000" />}
                      title="Send Notifications"
                      subtitle="Update customers about payments, approvals, or overdue amounts."
                    />
                  </div>
                  <div className="w-[49%]">
                    <ActionCard
                      icon={<FileText color="#000000" />}
                      title="Generate Reports"
                      subtitle="Produce reports for compliance, audits, or performance reviews."
                    />
                  </div>
                </div>
                <div className="flex items-center my-[20px] justify-between">
                  <div className="w-[49%]">
                    <ActionCard
                      icon={<Siren color="#000000" />}
                      title="Freeze/Cancel Loan Application"
                      subtitle="Suspend or terminate a loan due to fraud, default, or other issues."
                    />
                  </div>
                </div> */}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default LoanApplication;
